import '@/styles/product-delivery.css';
import { ProductLabels, ProductLabelOverlay, ProductUnavailableNotice } from '@/components/product/ProductLabels';
import { productUnavailableReason } from '@/lib/productLabels';
import { useState, useEffect, useMemo, useCallback, useRef, Suspense, type MouseEvent } from 'react';
import { googleRating, type GoogleReviewsData as ReviewsData } from '@/lib/googleReviews';
import { createPortal } from 'react-dom';
import { isPolishHoliday, skipHolidays } from '@/lib/polishHolidays';
import { polishDateLabel, formatDateDDMM, firstSlotStartHour, warsawDetailed } from '@/lib/useDeliveryCountdown';
import { useInView } from '@/lib/useInView';
import { useClientReady } from '@/lib/initialRender';
import { useIsMobile, useMediaQuery } from '@/hooks/use-mobile';
import { useParams, Link } from 'wouter';
import {
  ArrowLeft, ShoppingBag, Check, Loader2, Package,
  X, ChevronLeft, ChevronRight, ChevronDown, Star, ZoomIn,
  Truck, Flower2, MessageCircleHeart, Clock, Calendar,
  Plus, Gift, Heart, Sparkles, MapPin, ShieldCheck,
} from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { isValidInternationalPhone, phoneValidationMessage } from '@/lib/phone';
import { useSeo, stripHtml } from '@/lib/useSeo';
import { useGoogleAnalyticsEvent } from '@/components/GoogleAdsMeasurement';
import { analyticsItems } from '@/lib/googleAnalytics';
import { displayText } from '@/lib/displayText';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useImageMagnifier } from '@/hooks/useImageMagnifier';
import DOMPurify from 'dompurify';
import { serializeCmsJson } from '@workspace/url-source/cms';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { findBreadcrumbParent } from '@/components/sections/megaMenuData';
import { handleFromPublicUrl, isAddonProduct, productBreadcrumbParent, productUrl } from '@workspace/url-source';
import { consumeCollectionContext } from '@/lib/collectionContext';
import { useCart, type SavedDelivery } from '@/context/CartContext';
import { priceAmountGrosze } from '@/lib/cartMoney';
import { lazyWithReload } from '@/lib/lazyWithReload';
import {
  fetchProductByHandle, fetchProducts, formatPrice, productDisplayPrice, getCachedProduct,
  type ShopifyProductFull, type ShopifyVariantFull, type ShopifyProduct,
  type DeliveryDetails, type DeliveryCostResult,
  parseShortDescription,
} from '@/lib/shopifyApi';
import { fetchCollection } from '@/lib/shopifyApi.eager';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { isPrerenderMode } from '@/lib/isPrerenderMode';
import { setBottomNavHidden } from '@/lib/mobilePanels';
import { useScrollLock } from '@/lib/scrollLock';
import { REVIEW_CARD_STYLE, REVIEW_TEXT_STYLE, REVIEW_BUTTON_STYLE } from '@/lib/reviewStyles';
import {
  KATEGORIE_DODATKI,
  fetchAddonCatalog,
  initialAddonsByCategory,
  type CategoryId,
  type DodatekResolved,
} from '@/lib/addonCatalog';

/* ── Lazy: formularz dostawy montowany tylko po otwarciu modala ── */
const CheckoutDeliveryForm = lazyWithReload(() => import('@/components/ui/CheckoutDeliveryForm').then(m => ({ default: m.CheckoutDeliveryForm })));

/* ─── Design tokens ──────────────────────────────────────────── */
const GOLD       = '#e05590';
const GOLD_LIGHT = 'rgba(210,60,120,0.12)';
const DARK       = '#2a1f14';
const CREAM      = '#faf7f2';
const CREAM2     = '#ffffff';
const LINE       = 'rgba(210,60,120,0.2)';
const MUTED      = 'rgba(42,31,20,0.45)';
const GREEN      = '#2e8b47';
const ROSE       = '#9b2335';
const ROSE_LIGHT = 'rgba(155,35,53,0.08)';
const CART_CTA_RADIUS = 12;

function productCartCtaStyle(available: boolean): React.CSSProperties {
  return {
    width: '100%', height: 48, padding: '0 16px',
    background: available ? GOLD : 'rgba(42,31,20,0.15)',
    color: available ? '#fff' : MUTED,
    border: 'none', borderRadius: CART_CTA_RADIUS,
    boxShadow: 'none',
    cursor: available ? 'pointer' : 'not-allowed',
    fontFamily: 'DM Sans, sans-serif', fontSize: 13,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'background 0.15s, color 0.15s',
  };
}

function formatPickupSlot(slot: string): string {
  const [start, end] = slot.replace(/^p-/, '').split('-');
  if (!start || !end || !/^\d+$/.test(start) || !/^\d+$/.test(end)) return slot;
  return `${start.padStart(2, '0')}:00 – ${end.padStart(2, '0')}:00`;
}

/* ─── Responsive breakpoint helpers ─────────────────────────── */
function useIsTablet() {
  const [isTablet, setIsTablet] = useState(false);
  useEffect(() => {
    const check = () => setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isTablet;
}

/* ─── Delivery badge ─────────────────────────────────────────── */
const BADGE_SLOTS = [
  { label: '9:00–11:00',  cutoffHour: 8  },
  { label: '11:00–13:00', cutoffHour: 10 },
  { label: '13:00–15:00', cutoffHour: 12 },
  { label: '15:00–17:00', cutoffHour: 14 },
  { label: '17:00–19:00', cutoffHour: 16 },
  { label: '19:00–21:00', cutoffHour: 18 },
];

function getWarsawDateISO(): string {
  const parts = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year')?.value  ?? '2024';
  const m = parts.find(p => p.type === 'month')?.value ?? '01';
  const d = parts.find(p => p.type === 'day')?.value   ?? '01';
  return `${y}-${m}-${d}`;
}

function addOneDayISO(iso: string): string {
  const [y, mo, d] = iso.split('-').map(Number);
  const date = new Date(y, mo - 1, d + 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/* Sunday slots — must stay in sync with CheckoutDeliveryForm SUNDAY_SLOTS */
const SUNDAY_SLOTS = [
  { label: '10:00–13:00', cutoffHour: 9  },
  { label: '13:00–16:00', cutoffHour: 12 },
];

function isSundayISO(iso: string): boolean {
  const [y, mo, d] = iso.split('-').map(Number);
  return new Date(y, mo - 1, d).getDay() === 0;
}

/* ─── Decorative flower branch (inline SVG) ──────────────────── */
function FlowerDeco({ urgent, calendar }: { urgent?: boolean; calendar?: boolean }) {
  const petal  = urgent ? '#e05570' : calendar ? '#f0a8bc' : '#e8a0b8';
  const center = urgent ? '#c0392b' : calendar ? '#c9a24a' : '#d4a94e';
  const stem   = calendar ? '#2d6a4f' : urgent ? '#c0637a' : '#b08a3e';
  const fillMode = calendar ? 'none' : petal;
  return (
    <svg width="54" height="46" viewBox="0 0 54 46" fill="none" aria-hidden="true"
      style={{ position: 'absolute', top: 6, right: 8, opacity: 0.9, pointerEvents: 'none' }}>
      {/* stem + leaves */}
      <path d="M10 42 C 20 34, 32 26, 44 12" stroke={stem} strokeWidth="1.3" strokeLinecap="round" />
      <path d="M22 32 C 18 28, 18 24, 22 22 C 26 24, 26 28, 22 32Z"
        stroke={stem} strokeWidth="1.1" fill={calendar ? 'none' : 'rgba(120,150,90,0.25)'} />
      <path d="M33 22 C 37 18, 41 18, 43 22 C 41 26, 37 26, 33 22Z"
        stroke={stem} strokeWidth="1.1" fill={calendar ? 'none' : 'rgba(120,150,90,0.2)'} />
      {/* big flower */}
      <g stroke={petal} strokeWidth={calendar ? 1.2 : 0} fill={fillMode}>
        <circle cx="44" cy="9" r="3.2" />
        <circle cx="49" cy="12" r="3.2" />
        <circle cx="47" cy="17.5" r="3.2" />
        <circle cx="41" cy="17.5" r="3.2" />
        <circle cx="39" cy="12" r="3.2" />
      </g>
      <circle cx="44" cy="13.5" r="2.4" fill={calendar ? 'none' : center} stroke={center} strokeWidth="1.1" />
      {/* small bud */}
      <g stroke={petal} strokeWidth={calendar ? 1.1 : 0} fill={fillMode}>
        <circle cx="27" cy="27" r="2.1" />
        <circle cx="30.5" cy="29" r="2.1" />
        <circle cx="28.5" cy="32.3" r="2.1" />
        <circle cx="25" cy="30.5" r="2.1" />
      </g>
      <circle cx="27.8" cy="29.6" r="1.4" fill={calendar ? 'none' : center} stroke={center} strokeWidth="0.9" />
    </svg>
  );
}

/* ─── Countdown digit block ──────────────────────────────────── */
function BadgeTimeUnit({ value, unit, color, unitColor }: { value: string; unit: string; color: string; unitColor: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 34 }}>
      <span style={{
        fontFamily: 'DM Mono, DM Sans, monospace',
        fontSize: 26, fontWeight: 700, lineHeight: 1.05,
        color, fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </span>
      <span style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: 9,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        color: unitColor, fontWeight: 600, marginTop: 2,
      }}>
        {unit}
      </span>
    </div>
  );
}

function DeliveryBadge() {
  const clientReady = useClientReady();
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1_000);
    return () => clearInterval(id);
  }, []);

  /* viewport width — switches to shorter copy on narrow screens so the row never wraps or clips */
  const narrow = useMediaQuery('(max-width: 459px)');

  // Delivery times must use the visitor's current clock after hydration.
  if (!clientReady) return null;

  const { hour, minute, second } = warsawDetailed();
  const totalMin  = hour * 60 + minute;
  const totalSec  = totalMin * 60 + second;
  const nightMode = totalMin < 6 * 60;                                      // 00:00–05:59
  const todayISO  = getWarsawDateISO();
  const [cy, cm, cd] = todayISO.split('-').map(Number);
  const todayIsHoliday = isPolishHoliday(new Date(cy, cm - 1, cd));
  const slots     = isSundayISO(todayISO) ? SUNDAY_SLOTS : BADGE_SLOTS;
  const next      = todayIsHoliday ? undefined : slots.find(s => totalMin < s.cutoffHour * 60);
  const minsLeft  = next ? next.cutoffHour * 60 - totalMin : 0;
  const urgent    = !nightMode && !!next && minsLeft <= 60;

  /* seconds until cutoff, hh/mm/ss */
  const secsLeft = next ? Math.max(0, next.cutoffHour * 3600 - totalSec) : 0;
  const hh = String(Math.floor(secsLeft / 3600)).padStart(2, '0');
  const mm = String(Math.floor((secsLeft % 3600) / 60)).padStart(2, '0');
  const ss = String(secsLeft % 60).padStart(2, '0');

  /* Palette per state */
  const PLUM   = '#8b1a3a';
  const RED    = '#c0392b';
  const GOLD_D = '#7a5c1e';
  const GREEN_D = '#2d6a4f';

  const isCalendar = !next;
  const isNight    = !todayIsHoliday && nightMode && !!next;
  const showTimer  = !!next && !isNight;

  const cardBg = urgent ? '#fff0f3' : isCalendar ? '#fffdf8' : '#ffffff';
  const cardBorder = urgent
    ? 'rgba(220,70,100,0.55)'
    : isCalendar
      ? 'rgba(45,106,79,0.4)'
      : 'rgba(200,150,90,0.45)';

  /* calendar-state texts */
  let calDayLabel = '', calDateDDMM = '', calStartHour = '';
  if (isCalendar) {
    const tomorrowISO = addOneDayISO(todayISO);
    const [ty, tm, td] = tomorrowISO.split('-').map(Number);
    const tomorrowIsHoliday = isPolishHoliday(new Date(ty, tm - 1, td));
    const nextISO = tomorrowIsHoliday ? skipHolidays(tomorrowISO) : tomorrowISO;
    calDayLabel  = polishDateLabel(nextISO, todayISO);
    calDateDDMM  = formatDateDDMM(nextISO);
    calStartHour = firstSlotStartHour(nextISO);
  }

  /* icon circle */
  const circleBg = urgent
    ? 'rgba(220,70,100,0.14)'
    : isCalendar
      ? 'rgba(45,106,79,0.12)'
      : 'linear-gradient(135deg, #f5c26b, #e8a04a)';
  const iconColor = urgent ? RED : isCalendar ? GREEN_D : '#ffffff';

  const headline = urgent
    ? 'OSTATNIE MINUTY NA DOSTAWĘ DZIŚ!'
    : isCalendar
      ? 'NAJBLIŻSZA MOŻLIWA DOSTAWA'
      : isNight
        ? 'DOSTAWA JUŻ DZIŚ'
        : 'DOSTAWA JESZCZE DZIŚ';
  const headlineColor = urgent ? RED : isCalendar ? GREEN_D : GOLD_D;
  const timerColor = urgent ? RED : PLUM;
  const unitColor  = urgent ? RED : MUTED;

  /* night state: time until first slot start (9:00) */
  const nightSecsToStart = isNight ? Math.max(0, 9 * 3600 - totalSec) : 0;
  const nightH = Math.floor(nightSecsToStart / 3600);
  const nightM = Math.floor((nightSecsToStart % 3600) / 60);

  return (
    <div style={{
      position: 'relative',
       display: 'flex', alignItems: 'center', gap: narrow ? 4 : 6,
      flexWrap: 'nowrap', whiteSpace: 'nowrap',
      justifyContent: 'flex-start',
       fontSize: narrow ? 'clamp(9px, calc(3.1vw - 1px), 11px)' : 13,
      marginBottom: 14,
       padding: narrow ? '8px' : '10px 12px',
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: 12,
      maxWidth: '100%',
      boxSizing: 'border-box',
    }}>
      {!narrow && (
        <Clock size={16} color={urgent ? RED : isCalendar ? GREEN_D : GOLD_D} style={{ flexShrink: 0 }} />
      )}
      {isCalendar ? (
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1em', color: DARK }}>
          <span style={{ fontWeight: 700 }}>
            {narrow ? '' : 'Najbliższa dostawa: '}
            {calDayLabel.charAt(0).toUpperCase() + calDayLabel.slice(1)}, {calDateDDMM}
          </span>
          {' '}
          <span style={{ color: GOLD, fontWeight: 600 }}>od {calStartHour}</span>
        </span>
      ) : (
        <>
          <span style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '1em', fontWeight: 700,
            color: urgent ? RED : PLUM,
          }}>
            Najbliższa dostawa dziś {next!.label.replace('–', '-')}
          </span>
          {showTimer && (
            <span style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '0.9em', fontWeight: 600,
              color: urgent ? RED : MUTED, fontVariantNumeric: 'tabular-nums',
            }}>
              {narrow
                ? `zamów w ciągu ${Number(hh)}g ${Number(mm)}m`
                : `zamów w ciągu ${Number(hh)} godz. ${Number(mm)} min.`}
            </span>
          )}
        </>
      )}
    </div>
  );
}

/** Collection titles/handles containing add-on products — used to block their product pages. */
const ADDON_COLLECTION_KEYS = new Set<string>(
  KATEGORIE_DODATKI.map(cat => cat.collectionTitle.toLocaleLowerCase('pl-PL')),
);

function selectInitialVariant(
  product: ShopifyProductFull | null,
  collectionCtx: { href?: string; label?: string } | null,
): ShopifyVariantFull | null {
  const variants = product?.variants.nodes ?? [];
  const autoSelectFirst = (
    (collectionCtx?.href ?? '').toLowerCase().includes('doniczkowe') ||
    (collectionCtx?.label ?? '').toLowerCase().includes('doniczkowe') ||
    (product?.tags ?? []).some(tag => tag.toLowerCase().includes('doniczkowe')) ||
    (collectionCtx?.href ?? '').toLowerCase().includes('sztuczne-kompozycje') ||
    (collectionCtx?.label ?? '').toLowerCase().includes('sztuczne') ||
    (product?.tags ?? []).some(tag => tag.toLowerCase().includes('sztuczne-kompozycje'))
  );

  if (autoSelectFirst) {
    return variants.find(variant => variant.availableForSale) ?? variants[0] ?? null;
  }

  return variants.find(variant => variant.title === 'Średni' && variant.availableForSale)
    ?? variants.find(variant => variant.title === 'Średni')
    ?? variants.find(variant => variant.availableForSale)
    ?? variants[0]
    ?? null;
}


const OCCASIONS = [
  { emoji: '❤️', label: 'Rocznica' },
  { emoji: '🎂', label: 'Urodziny' },
  { emoji: '💐', label: 'Podziękowanie' },
  { emoji: '🌹', label: 'Zaręczyny' },
  { emoji: '🎉', label: 'Gratulacje' },
  { emoji: '🍼', label: 'Narodziny' },
];

const WHY_ITEMS = [
  { icon: <Flower2 size={22} color={GOLD} />, title: 'Świeże kwiaty każdego dnia', desc: 'Dostarczamy wyłącznie kwiaty z dnia.' },
  { icon: <Truck size={22} color={GOLD} />, title: 'Lokalna dostawa', desc: 'Dowieziemy do 20 km w ciągu kilku godzin.' },
  { icon: <MessageCircleHeart size={22} color={GOLD} />, title: 'Pełna personalizacja', desc: 'Bilecik, dodatki, własna kompozycja.' },
  { icon: <Star size={22} color={GOLD} fill={GOLD} />, title: 'Starannie zapakowane', desc: 'Każdy bukiet w ekskluzywnym opakowaniu.' },
];

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, '');


/* ─── Gallery with lightbox ──────────────────────────────────── */
function ProductGallery({ images, tags = [] }: { images: Array<{ url: string; altText: string | null }>; tags?: string[] }) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  // On mobile and tablet we hide the left sidebar and use a horizontal strip instead
  const useSidebarThumbs = !isMobile && !isTablet;
  const isBestseller = tags.some(t => t.toLowerCase() === 'bestseller');
  /* Mobile thumbnail strip: show at most 5 tiles; last tile gets a "+N" overlay */
  const MAX_MOBILE_THUMBS = 5;

  useScrollLock(lightbox);

  if (images.length === 0) {
    return (
      <div style={{ aspectRatio: '4/5', background: CREAM2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Package size={48} color={MUTED} />
      </div>
    );
  }

  const safe = Math.min(active, images.length - 1);
  const prev = () => setActive(i => (i - 1 + images.length) % images.length);
  const next = () => setActive(i => (i + 1) % images.length);
  const closeLightbox = () => setLightbox(false);
  const lightboxRef = useFocusTrap<HTMLDivElement>(lightbox, closeLightbox);
  const magnifier = useImageMagnifier();

  return (
    <>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, alignItems: 'flex-start' }}>
        {/* Thumbnails — left column on desktop only (hidden on tablet + mobile) */}
        {useSidebarThumbs && images.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0, width: 140 }}>
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Zdjęcie ${i + 1} z ${images.length}`}
                aria-current={i === safe}
                style={{
                  width: 140, height: 140, overflow: 'hidden', padding: 0,
                  border: `2px solid ${i === safe ? GOLD : 'transparent'}`,
                  background: CREAM2, cursor: 'pointer',
                  transition: 'border-color 0.18s', outline: 'none', flexShrink: 0,
                }}
              >
                <img src={img.url} alt={img.altText ?? `Zdjęcie ${i + 1} produktu`} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
              </button>
            ))}
          </div>
        )}

        {/* Main image */}
        <div
          ref={magnifier.containerRef}
          onMouseEnter={magnifier.onMouseEnter}
          onMouseMove={magnifier.onMouseMove}
          onMouseLeave={magnifier.onMouseLeave}
          style={{
            position: 'relative', flex: 1,
            width: '100%', boxSizing: 'border-box',
            margin: 0,
            aspectRatio: '4/5', overflow: 'hidden', background: CREAM2,
            borderRadius: isMobile ? 12 : 0,
          }}>
          <img
            src={images[safe].url}
            alt={images[safe].altText ?? 'Zdjęcie produktu'}
            onClick={() => setLightbox(true)}
            style={{
              width: '100%', height: '100%', objectFit: 'contain', display: 'block', cursor: 'zoom-in',
              opacity: magnifier.isZooming ? 0 : 1,
              transition: 'opacity 0.15s ease',
            }}
          />
          {magnifier.enabled && (
            <img
              ref={magnifier.zoomImgRef}
              src={images[safe].url}
              alt=""
              aria-hidden
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%', objectFit: 'contain', display: 'block',
                transformOrigin: '0 0',
                opacity: magnifier.isZooming ? 1 : 0,
                transition: 'opacity 0.15s ease',
                pointerEvents: 'none',
              }}
            />
          )}
          {/* BESTSELLER badge — mobile, top-left */}
          {isMobile && isBestseller && (
            <span style={{
              position: 'absolute', top: 12, left: 12,
              background: '#e8871e', color: '#fff',
              fontFamily: 'DM Sans, sans-serif', fontSize: 10, fontWeight: 700,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '5px 10px', borderRadius: 999,
            }}>Bestseller</span>
          )}
          {/* Zoom button — mobile, top-right */}
          {isMobile && (
            <button
              onClick={() => setLightbox(true)}
              aria-label="Powiększ zdjęcie"
              style={{
                position: 'absolute', top: 12, right: 12,
                background: 'rgba(255,255,255,0.92)', border: 'none',
                width: 40, height: 40, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              }}
            >
              <ZoomIn size={17} color={DARK} />
            </button>
          )}
          {/* Image counter — mobile, bottom-right */}
          {isMobile && images.length > 1 && (
            <span style={{
              position: 'absolute', bottom: 12, right: 12,
              background: 'rgba(255,255,255,0.92)',
              fontFamily: 'DM Sans, sans-serif', fontSize: 12, fontWeight: 600,
              color: DARK, padding: '4px 10px', borderRadius: 999,
            }}>{safe + 1}/{images.length}</span>
          )}
          {/* Nav arrows (only if multiple, desktop/tablet) */}
          {!isMobile && images.length > 1 && (
            <>
              <button onClick={prev} aria-label="Poprzednie zdjęcie" style={arrowStyle('left')}>
                <ChevronLeft size={18} color={DARK} />
              </button>
              <button onClick={next} aria-label="Następne zdjęcie" style={arrowStyle('right')}>
                <ChevronRight size={18} color={DARK} />
              </button>
            </>
          )}
          {/* Watermark */}
          {!isMobile && (
            <span style={{
              position: 'absolute', top: 14, left: 18,
              fontFamily: 'Cormorant Garamond, serif', fontSize: 11,
              color: 'rgba(255,255,255,0.5)', letterSpacing: '0.22em', textTransform: 'uppercase',
            }}>Bukietowa</span>
          )}
        </div>

        {/* Horizontal thumbnail strip — mobile and tablet */}
        {!useSidebarThumbs && images.length > 1 && (
          <div style={{
            display: 'flex', gap: 8, overflowX: 'auto', width: '100%',
            paddingBottom: 4,
            /* hide scrollbar on webkit but keep functionality */
            WebkitOverflowScrolling: 'touch',
          }}>
            {(isMobile ? images.slice(0, MAX_MOBILE_THUMBS) : images).map((img, i) => {
              const hidden = isMobile ? images.length - MAX_MOBILE_THUMBS : 0;
              const isLastVisible = isMobile && i === MAX_MOBILE_THUMBS - 1 && hidden > 0;
              return (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`Zdjęcie ${i + 1} z ${images.length}`}
                  aria-current={i === safe}
                  style={{
                    width: 72, height: 72,
                    minWidth: 72, flexShrink: 0,
                    overflow: 'hidden', padding: 0, position: 'relative',
                    border: `2px solid ${i === safe ? GOLD : 'transparent'}`,
                    borderRadius: isMobile ? 10 : 0,
                    background: CREAM2, cursor: 'pointer',
                    transition: 'border-color 0.18s', outline: 'none',
                  }}
                >
                  <img src={img.url} alt={img.altText ?? `Zdjęcie ${i + 1} produktu`} style={{ width: '100%', height: '100%', objectFit: isMobile ? 'cover' : 'contain', display: 'block' }} />
                  {isLastVisible && (
                    <span style={{
                      position: 'absolute', inset: 0,
                      background: 'rgba(20,15,10,0.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontFamily: 'DM Sans, sans-serif',
                      fontSize: 15, fontWeight: 700,
                    }}>+{hidden}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox — portal to document.body to escape stacking contexts */}
      {lightbox && createPortal(
        <div
          ref={lightboxRef}
          role="dialog"
          aria-modal="true"
          aria-label="Powiększone zdjęcie produktu"
          onClick={closeLightbox}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,8,5,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 99999, padding: 20,
          }}
        >
          <img
            src={images[safe].url}
            alt={images[safe].altText ?? 'Zdjęcie produktu'}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', display: 'block' }}
          />
          <button
            onClick={closeLightbox}
            aria-label="Zamknij powiększenie"
            style={{
              position: 'absolute', top: 20, right: 20,
              background: 'rgba(255,255,255,0.1)', border: 'none',
              width: 44, height: 44, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={20} color="#fff" />
          </button>
          {images.length > 1 && (
            <>
              <button onClick={e => { e.stopPropagation(); prev(); }} aria-label="Poprzednie zdjęcie" style={{ ...arrowStyle('left'), top: '50%', transform: 'translateY(-50%)', position: 'fixed' }}>
                <ChevronLeft size={20} color={DARK} />
              </button>
              <button onClick={e => { e.stopPropagation(); next(); }} aria-label="Następne zdjęcie" style={{ ...arrowStyle('right'), top: '50%', transform: 'translateY(-50%)', position: 'fixed' }}>
                <ChevronRight size={20} color={DARK} />
              </button>
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

function arrowStyle(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'absolute', top: '50%', [side]: 8,
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.88)', border: 'none',
    width: 44, height: 44, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', backdropFilter: 'blur(4px)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  };
}

/* ─── Star rating — live Google data, scrolls to reviews ────── */
function StarRating({ compact }: { compact?: boolean }) {
  const [data, setData] = useState<ReviewsData | null>(null);

  useEffect(() => {
    fetch(`${BASE_URL}/api/reviews/google`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((d: ReviewsData) => setData(d))
      .catch(() => {});
  }, []);

  const rating = googleRating(data);
  if (!rating) return null;
  const { score } = rating;
  const count = rating.total.toLocaleString('pl-PL');

  const handleClick = () => {
    const el = document.getElementById('opinie-klientow');
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <button
      onClick={handleClick}
      aria-label={`Opinie o kwiaciarni w Google: ${score} na 5, ${count} opinii`}
      style={{
        display: 'flex', alignItems: 'center', gap: compact ? 5 : 8,
        marginBottom: compact ? 0 : 16, background: 'none', border: 'none',
        padding: 0, cursor: 'pointer', flexShrink: 0,
        whiteSpace: 'nowrap', flexWrap: 'nowrap',
      }}
    >
      <div style={{ display: 'flex', gap: 2 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <Star key={n} size={compact ? 13 : 14} color={GOLD} fill={n <= Math.round(rating.rating) ? GOLD : 'transparent'} />
        ))}
      </div>
      <span style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: compact ? 12 : 13, color: MUTED,
        textDecoration: 'underline', textUnderlineOffset: 3,
        textDecorationColor: 'rgba(42,31,20,0.2)',
      }}>
        {compact ? `(${count} opinii)` : `${score} (${count} opinii)`}
      </span>
    </button>
  );
}

/* ─── Trust badges ───────────────────────────────────────────── */
function TrustBadges() {
  const badges: { icon: React.ReactNode; text: string }[] = [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '20px 0 24px' }}>
      {badges.map(b => (
        <div key={b.text} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {b.icon}
          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'rgba(42,31,20,0.65)' }}>
            {b.text}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Bouquet size icon ──────────────────────────────────────── */
const BOUQUET_IMAGES: Record<string, string> = {
  'Mały':    `${BASE_URL}/bouquet-maly.png`,
  'Sredni':  `${BASE_URL}/bouquet-sredni.png`,
  'Średni':  `${BASE_URL}/bouquet-sredni.png`,
  'Duzy':    `${BASE_URL}/bouquet-duzy.png`,
  'Duży':    `${BASE_URL}/bouquet-duzy.png`,
  'Premium': `${BASE_URL}/bouquet-premium.png`,
};

const BOUQUET_ICON_HEIGHT: Record<string, number> = {
  'Mały':    80,
  'Sredni':  98,
  'Średni':  98,
  'Duzy':    110,
  'Duży':    110,
  'Premium': 130,
};
const BOUQUET_ICON_WIDTH: Record<string, number> = {
  'Mały':    76,
  'Sredni':  94,
  'Średni':  94,
  'Duzy':    106,
  'Duży':    106,
  'Premium': 120,
};

function BouquetSizeIcon({ val, selected, available, small }: { val: string; selected: boolean; available: boolean; small?: boolean }) {
  const src = BOUQUET_IMAGES[val] ?? BOUQUET_IMAGES['Mały'];
  const scale = small ? 0.48 : 1;
  const h = (BOUQUET_ICON_HEIGHT[val] ?? 82) * scale;
  const w = (BOUQUET_ICON_WIDTH[val] ?? 80) * scale;
  return (
    <div style={{
      width: w,
      height: small ? 64 : 130,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
    }}>
      <img
        src={src}
        alt={val}
        style={{
          width: w,
          height: h,
          objectFit: 'contain',
          objectPosition: 'bottom center',
          filter: !available ? 'grayscale(1) opacity(0.45)' : 'none',
          opacity: !available ? 0.5 : 1,
          transition: 'opacity 0.18s, filter 0.18s',
          display: 'block',
        }}
      />
    </div>
  );
}

/* ─── Variant selector ───────────────────────────────────────── */
/* Flower counts per size name (defaults).
   Override by putting the count anywhere in the variant title, e.g. "Mały — 9 szt."
   or encoding it as "9" in the variant SKU field.                                        */
const SIZE_FLOWER_DEFAULTS: Record<string, { count: string; note: string }> = {
  'Mały':    { count: '7–9',   note: 'kwiatów' },
  'Sredni':  { count: '12–15', note: 'kwiatów' },
  'Średni':  { count: '12–15', note: 'kwiatów' },
  'Duzy':    { count: '18–22', note: 'kwiatów' },
  'Duży':    { count: '18–22', note: 'kwiatów' },
  'Premium': { count: '25–30', note: 'kwiatów' },
};

/* Detect if an option name represents a size dimension */
const isSizeOption = (name: string) =>
  /rozmiar|size|wielko/i.test(name);

/* Parse a flower count override from a variant title, e.g. "Mały — 9 szt." → "9" */
function parseFlowerCount(title: string): string | null {
  const m = title.match(/(\d+(?:[–\-]\d+)?)\s*szt/i);
  return m ? m[1] : null;
}

/**
 * Split a variant option value on the em-dash separator " — ".
 * "Mały — 5 róż + 8 goździków + 2 gipsówki" → { label: "Mały", composition: "5 róż + 8 goździków + 2 gipsówki" }
 * "Mały" (no dash) → { label: "Mały", composition: null }
 */
function parseVariantLabel(val: string): { label: string; composition: string | null } {
  const idx = val.indexOf(' — ');
  if (idx === -1) return { label: val, composition: null };
  return { label: val.slice(0, idx).trim(), composition: val.slice(idx + 3).trim() };
}

function VariantSelector({
  variants, selected, onSelect,
}: {
  variants: ShopifyVariantFull[];
  selected: ShopifyVariantFull | null;
  onSelect: (v: ShopifyVariantFull) => void;
}) {
  const isMobile = useIsMobile();
  if (!variants.length) return null;
  const optionNames = variants[0]?.selectedOptions.map(o => o.name) ?? [];
  if (variants.length === 1 && variants[0].title === 'Default Title') return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {optionNames.map(optName => {
        const isSize = isSizeOption(optName);
        const values = [...new Set(variants.map(v => v.selectedOptions.find(o => o.name === optName)?.value ?? ''))];
        const selectedVal = selected?.selectedOptions.find(o => o.name === optName)?.value;

        /* Flower info for the currently selected size value */
        let flowerInfo: { count: string; note: string } | null = null;
        if (isSize && selectedVal) {
          const override = parseFlowerCount(selectedVal);
          const def = SIZE_FLOWER_DEFAULTS[selectedVal];
          flowerInfo = override
            ? { count: override, note: 'szt.' }
            : def ?? null;
        }
        /* Composition from variant name (after " — ") for selected value */
        const selectedComposition = isSize && selectedVal
          ? parseVariantLabel(selectedVal).composition
          : null;

        return (
          <div key={optName}>
            {/* Label row — hidden for size options (info visible on each card) */}
            {!isSize && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, fontWeight: 600, margin: 0 }}>
                  {displayText(optName)}
                  {selectedVal && (
                    <span style={{ fontWeight: 400, color: MUTED, letterSpacing: '0.12em', marginLeft: 6 }}>
                      - {displayText(selectedVal)}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Option tiles — 4-column grid on mobile size selector */}
            <div style={isSize && isMobile
              ? { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }
              : { display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {values.map(val => {
                const candidate = variants.find(v => {
                  const thisOpt = v.selectedOptions.find(o => o.name === optName)?.value;
                  if (thisOpt !== val) return false;
                  if (!selected) return true;
                  return v.selectedOptions.every(o => {
                    if (o.name === optName) return true;
                    const selVal = selected.selectedOptions.find(s => s.name === o.name)?.value;
                    return selVal === o.value;
                  });
                }) ?? variants.find(v => v.selectedOptions.find(o => o.name === optName)?.value === val);
                const isSelected = selectedVal === val;
                const available  = candidate?.availableForSale ?? false;
                const isPremium  = val === 'Premium';

                /* For size options: card with image, name, flower count, price */
                if (isSize) {
                  const { label: sizeLabel, composition } = parseVariantLabel(val);
                  const flowerDefault = SIZE_FLOWER_DEFAULTS[sizeLabel];
                  const flowerOverride = parseFlowerCount(val);
                  const flower = flowerOverride
                    ? { count: flowerOverride, note: 'szt.' }
                    : flowerDefault ?? null;
                  const priceLabel = candidate?.price.amount
                    ? `${Math.round(parseFloat(candidate.price.amount))} zł`
                    : null;
                  return (
                    <button
                      key={val}
                      disabled={!available}
                      onClick={() => candidate && onSelect(candidate)}
                      onMouseEnter={e => { if (available) { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(210,60,120,0.22)'; } }}
                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                      style={{
                        flex: isMobile ? undefined : '1 1 0',
                        minWidth: isMobile ? 0 : 72,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'flex-start',
                        padding: isMobile ? '12px 4px 10px' : '14px 6px 12px',
                        border: isSelected ? `2px solid ${GOLD}` : `1.5px solid ${isMobile ? LINE : GOLD}`,
                        borderRadius: 'var(--config-card-radius)',
                        background: isSelected ? GOLD_LIGHT : '#fff',
                        cursor: available ? 'pointer' : 'not-allowed',
                        outline: 'none',
                        position: 'relative',
                        transition: 'border-color 0.18s, background 0.18s, transform 0.2s ease, box-shadow 0.2s ease',
                      }}
                    >
                      {isSelected && (
                        <div style={{
                          position: 'absolute', top: 6, right: 6,
                          width: 20, height: 20, borderRadius: '50%',
                          background: GREEN,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Check size={12} color="#fff" strokeWidth={2.5} />
                        </div>
                      )}
                      {/* Bouquet image */}
                      <BouquetSizeIcon val={sizeLabel} selected={isSelected} available={available} small={isMobile} />
                      {/* Size name — only the part before " — " */}
                      <p style={{
                        fontFamily: 'DM Sans, sans-serif', fontSize: isMobile ? 13 : 13, fontWeight: isMobile ? 700 : 600,
                        color: available ? DARK : MUTED, margin: isMobile ? '6px 0 1px' : '8px 0 2px',
                        textDecoration: available ? 'none' : 'line-through',
                      }}>
                        {displayText(sizeLabel)}
                      </p>
                      {/* Price */}
                      {priceLabel && (
                        <p style={{
                          fontFamily: 'DM Sans, sans-serif', fontSize: isMobile ? 12 : 13,
                          fontWeight: isMobile ? 700 : 500,
                          color: isMobile ? (available ? GOLD : MUTED) : (available ? DARK : MUTED),
                          margin: 0, whiteSpace: 'nowrap',
                        }}>
                          {isMobile && candidate?.price.amount
                            ? `${parseFloat(candidate.price.amount).toFixed(2).replace('.', ',')} zł`
                            : priceLabel}
                        </p>
                      )}
                    </button>
                  );
                }

                /* Default (non-size) tile */
                return (
                  <button
                    key={val}
                    disabled={!available}
                    onClick={() => candidate && onSelect(candidate)}
                    style={{
                      padding: isMobile ? '10px 16px' : '12px 24px',
                      border: `${isSelected ? 2 : 1}px solid ${GOLD}`,
                       borderRadius: 'var(--config-card-radius)',
                      background: isSelected ? GOLD_LIGHT : isPremium ? 'rgba(210,60,120,0.06)' : '#fff',
                      color: isSelected ? DARK : available ? 'rgba(42,31,20,0.75)' : MUTED,
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: 14,
                      fontWeight: isSelected ? 600 : isPremium ? 500 : 400,
                      cursor: available ? 'pointer' : 'not-allowed',
                      transition: 'all 0.18s ease',
                      textDecoration: available ? 'none' : 'line-through',
                      letterSpacing: '0.03em',
                      position: 'relative',
                      minWidth: 80,
                      minHeight: 44,
                      display: 'flex', alignItems: 'center', gap: 6,
                      outline: 'none',
                    }}
                  >
                    {isPremium && <span style={{ fontSize: 13, lineHeight: 1 }}>🌸</span>}
                    {displayText(val)}
                    {isSelected && (
                      <span style={{
                        position: 'absolute', top: -1, right: -1,
                        width: 8, height: 8, borderRadius: '50%',
                        background: GOLD, display: 'block',
                      }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Composition / flower count panel — slides in when a size is selected */}
            {isSize && (
              <div style={{
                overflow: 'hidden',
                maxHeight: (selectedComposition || flowerInfo) ? 80 : 0,
                transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
              }}>
                {(selectedComposition || flowerInfo) && (
                  <div style={{
                    marginTop: 10,
                    padding: isMobile ? '8px 14px' : '10px 16px',
                    background: GOLD_LIGHT,
                    border: `1px solid ${isMobile ? GOLD : LINE}`,
                       borderRadius: 'var(--config-card-radius)',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <Flower2 size={15} color={GOLD} strokeWidth={1.5} />
                    <span style={{
                      fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK,
                    }}>
                      {selectedComposition
                        ? displayText(selectedComposition)
                        : (<><strong style={{ fontWeight: 600 }}>{flowerInfo!.count}</strong>{' '}{flowerInfo!.note} w bukiecie</>)
                      }
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Step row wrapper ───────────────────────────────────────── */
const ERROR_RED = '#c0392b';

function StepRow({ n, title, last, completed, error, children }: {
  n: number; title: string; last?: boolean; completed?: boolean; error?: boolean; children: React.ReactNode;
}) {
  const accent = error ? ERROR_RED : completed ? GREEN : DARK;
  return (
    <div style={{ display: 'flex', gap: 16 }}>
      {/* Left: numbered circle + vertical connector */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, paddingTop: 2 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          border: completed ? 'none' : `1.5px solid ${accent}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'DM Sans, sans-serif', fontSize: 12,
          color: accent, fontWeight: 500,
          background: '#fff', flexShrink: 0,
          transition: 'border-color 0.25s, color 0.25s',
        }}>
          {completed
            ? <span style={{
                width: 20, height: 20, borderRadius: '50%', background: GREEN,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Check size={12} color="#fff" strokeWidth={2.5} /></span>
            : n}
        </div>
        {!last && (
          <div style={{ flex: 1, width: 1, background: LINE, marginTop: 6, minHeight: 24 }} />
        )}
      </div>
      {/* Right: step label + content */}
      <div style={{ flex: 1, minWidth: 0, paddingBottom: last ? 0 : 28 }}>
        <p style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: 12,
          letterSpacing: '0.16em', textTransform: 'uppercase',
          color: accent, margin: '4px 0 12px', fontWeight: 700,
          transition: 'color 0.25s',
        }}>
          {title}
        </p>
        {children}
      </div>
    </div>
  );
}

/* ─── Mobile step accordion ──────────────────────────────────── */
function MobileStepAccordion({ n, title, icon, completed, error, children }: {
  n: number; title: string; icon: React.ReactNode;
  completed?: boolean; error?: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const accent = error ? ERROR_RED : DARK;
  return (
    <div style={{ borderTop: `1px solid rgba(42,31,20,0.1)` }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '15px 2px', background: 'none', border: 'none',
          cursor: 'pointer', outline: 'none', textAlign: 'left',
        }}
      >
        <span style={{
          width: 24, height: 24, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {completed
            ? <span style={{
                width: 20, height: 20, borderRadius: '50%', background: GREEN,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Check size={12} color="#fff" strokeWidth={2.5} /></span>
            : icon}
        </span>
        <span style={{
          flex: 1, fontFamily: 'DM Sans, sans-serif', fontSize: 14,
          fontWeight: 600, color: accent,
        }}>
          {n}. {title}
        </span>
        <ChevronDown size={18} color={MUTED} style={{
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.25s ease', flexShrink: 0,
        }} />
      </button>
      <div style={{
        overflow: 'hidden',
        maxHeight: open ? 2000 : 0,
        transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{ padding: '2px 2px 18px' }}>{children}</div>
      </div>
    </div>
  );
}

/* ─── Step shell — StepRow on desktop, accordion on mobile ───── */
function StepShell({ n, title, mobileTitle, icon, last, completed, error, children }: {
  n: number; title: string; mobileTitle?: string; icon: React.ReactNode;
  last?: boolean; completed?: boolean; error?: boolean; children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <MobileStepAccordion n={n} title={mobileTitle ?? title} icon={icon} completed={completed} error={error}>
        {children}
      </MobileStepAccordion>
    );
  }
  return (
    <StepRow n={n} title={title} last={last} completed={completed} error={error}>
      {children}
    </StepRow>
  );
}

/* ─── Category tile icon ─────────────────────────────────────── */
function CategoryIcon({ id }: { id: CategoryId }) {
  const s = { color: '#8b6e3a', strokeWidth: 1.2 } as const;
  if (id === 'wazony')     return <svg viewBox="0 0 40 48" fill="none" stroke="#8b6e3a" strokeWidth="1.2" strokeLinecap="round" style={{ width: 40, height: 48 }}><path d="M14 8 Q10 16 8 26 Q6 36 12 42 Q16 46 20 46 Q24 46 28 42 Q34 36 32 26 Q30 16 26 8"/><line x1="12" y1="8" x2="28" y2="8"/><path d="M15 4 Q20 2 25 4" strokeWidth="1"/></svg>;
  if (id === 'pluszaki')   return <Heart size={38} {...s} />;
  if (id === 'czekoladki') return <Gift size={38} {...s} />;
  if (id === 'toppery')    return <Sparkles size={38} {...s} />;
  if (id === 'alkohole')   return <svg viewBox="0 0 40 48" fill="none" stroke="#8b6e3a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 38, height: 46 }}><path d="M14 6 Q10 14 10 20 Q10 28 20 30 Q30 28 30 20 Q30 14 26 6Z"/><line x1="20" y1="30" x2="20" y2="42"/><line x1="13" y1="42" x2="27" y2="42"/><path d="M10 14 Q20 18 30 14" strokeWidth="0.9" opacity="0.5"/></svg>;
  return null;
}

/* ─── Category tile — card style matching reference ──────────── */
function CategoryTile({
  cat, active, onClick, selectedCount,
}: {
  cat: typeof KATEGORIE_DODATKI[number];
  active: boolean;
  onClick: () => void;
  selectedCount: number;
}) {
  // Wszystkie 5 kategorii stoją teraz zawsze w jednym rzędzie (5-kolumnowa
  // siatka, patrz PersonalizationSection) — na wąskim mobile to ciaśniejsza
  // kolumna niż na desktopie, więc kafelek dostaje mniejszy padding/czcionkę
  // i pomniejszoną (przeskalowaną) ikonę, żeby się swobodnie mieścił.
  const isMobile = useIsMobile();

  return (
    <button
      onClick={onClick}
      style={{
        position: 'relative', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? '10px 2px 8px' : '16px 8px 14px',
        gap: isMobile ? 4 : 8,
        border: `1.5px solid ${active ? ROSE : 'rgba(210,60,120,0.22)'}`,
        borderRadius: isMobile ? 10 : 14,
        background: active ? ROSE_LIGHT : '#fffcfa',
        boxShadow: active ? 'none' : '0 2px 8px rgba(155,35,53,0.07)',
        cursor: 'pointer', textAlign: 'center',
        outline: 'none', overflow: 'hidden',
        transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
        minWidth: 0,
      }}
    >
      {/* Icon */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transform: isMobile ? 'scale(0.62)' : undefined,
      }}>
        <CategoryIcon id={cat.id as CategoryId} />
      </div>

      {/* Name */}
      <p style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: isMobile ? 10 : 12, fontWeight: 600,
        color: DARK, margin: 0, lineHeight: 1.2,
      }}>
        {cat.name}
      </p>

      {/* +/- toggle button */}
      <div style={{
        width: isMobile ? 20 : 26, height: isMobile ? 20 : 26, borderRadius: '50%',
        border: `1.5px solid ${active ? ROSE : GOLD}`,
        background: active ? ROSE : '#fff',
        boxShadow: active ? 'none' : '0 1px 5px rgba(224,85,144,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {active
          ? <X size={isMobile ? 10 : 12} strokeWidth={2.5} color="#fff" />
          : <Plus size={isMobile ? 11 : 13} strokeWidth={2.5} color={GOLD} />}
      </div>

      {/* Selection count badge */}
      {selectedCount > 0 && (
        <div style={{
          position: 'absolute', top: 4, right: 4,
          width: isMobile ? 15 : 18, height: isMobile ? 15 : 18, borderRadius: '50%',
          background: ROSE,
          boxShadow: '0 1px 4px rgba(155,35,53,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontFamily: 'DM Sans', fontSize: isMobile ? 8.5 : 9.5, color: '#fff', fontWeight: 700 }}>
            {selectedCount}
          </span>
        </div>
      )}
    </button>
  );
}

/* ─── Dodatek product card ───────────────────────────────────── */
function DodatekProductCard({
  d, added, onAdd,
}: {
  d: DodatekResolved;
  added: boolean;
  onAdd: () => void;
}) {
  const unavailableReason = productUnavailableReason(d);
  const disabled = Boolean(unavailableReason) && !added;
  return (
    <div
      className="bk-labeled-product bk-labeled-product--compact"
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column',
        border: `1.5px solid ${added ? ROSE : 'rgba(210,60,120,0.18)'}`,
        background: '#fff',
        borderRadius: 10,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'border-color 0.2s',
      }}
    >
      <button
        type="button"
        aria-disabled={disabled}
        aria-label={`${d.name}${unavailableReason ? `: ${unavailableReason}` : ''}`}
        onClick={() => { if (!disabled) onAdd(); }}
        style={{ position: 'absolute', inset: 0, zIndex: 1, padding: 0, border: 0, borderRadius: 'inherit', background: 'transparent', cursor: disabled ? 'default' : 'pointer' }}
      />
      {/* Full-bleed image */}
      <div style={{ position: 'relative' }}>
      <div style={{
        width: '100%', aspectRatio: '3/4',
        background: CREAM,
        overflow: 'hidden', flexShrink: 0, borderRadius: '8px 8px 0 0',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {d.imageUrl ? (
          <img
            src={d.imageUrl} alt={d.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{ opacity: 0.25 }}>
            <Package size={32} color={GOLD} strokeWidth={1} />
          </div>
        )}
      </div>
      <ProductLabelOverlay product={d} />
      </div>

      {/* Price row + add button */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 10px',
      }}>
        <span style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: 13,
          color: ROSE, fontWeight: 600,
        }}>
          {d.price},00 zł
        </span>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          border: `1.5px solid ${added ? ROSE : ROSE}`,
          background: added ? ROSE : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          transition: 'background 0.2s',
        }}>
          {added
            ? <X size={13} strokeWidth={2.5} color="#fff" />
            : <Plus size={13} strokeWidth={2.5} color={ROSE} />}
        </div>
      </div>
    </div>
  );
}

/* ─── Personalization section ────────────────────────────────── */
function PersonalizationSection({
  note, onNote, selectedDodatki, onToggleDodatek, dodatkiByCategory,
}: {
  note: string;
  onNote: (v: string) => void;
  selectedDodatki: Set<string>;
  onToggleDodatek: (id: string) => void;
  dodatkiByCategory: Record<string, DodatekResolved[]>;
}) {
  const [activeCategory, setActiveCategory] = useState<CategoryId | null>(null);
  const lastCategoryRef = useRef<CategoryId | null>(null);
  const isMobile = useIsMobile();

  const handleTileClick = (id: CategoryId) => {
    setActiveCategory(prev => {
      if (prev === id) return null;
      lastCategoryRef.current = id;
      return id;
    });
  };

  // Use lastCategoryRef while closing so the grid stays populated during the animation
  const displayCategory = activeCategory ?? lastCategoryRef.current;
  const activeDodatki = displayCategory ? (dodatkiByCategory[displayCategory] ?? []) : [];

  const tileList = KATEGORIE_DODATKI.map(cat => {
    const count = (dodatkiByCategory[cat.id] ?? []).filter(d => selectedDodatki.has(d.id)).length;
    return (
      <CategoryTile
        key={cat.id}
        cat={cat}
        active={activeCategory === cat.id}
        onClick={() => handleTileClick(cat.id as CategoryId)}
        selectedCount={count}
      />
    );
  });

  return (
    <div>
      {/* ── Category tiles — zawsze wyśrodkowana siatka 5 równych kolumn,
          na każdej szerokości (dawniej mobile/tablet miały przewijany rząd
          z wygaszeniem na krawędzi — ostatni kafelek wyglądał na ucięty i
          "inny"; siatka pokazuje wszystkie naraz, równej wielkości). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: isMobile ? 5 : 8 }}>
        {tileList}
      </div>

      {/* Expanded product panel — slides in below tiles */}
      <div style={{
        overflow: 'hidden',
        maxHeight: activeCategory ? 700 : 0,
        transition: 'max-height 0.4s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div style={{
          border: `1.5px solid rgba(155,35,53,0.2)`,
          borderTop: 'none',
          borderRadius: '0 0 10px 10px',
          background: 'rgba(155,35,53,0.03)',
        }}>
          {/* Panel header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '1px solid rgba(155,35,53,0.12)',
          }}>
            <span style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: 11,
              fontWeight: 700, color: ROSE,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              {displayCategory
                ? (KATEGORIE_DODATKI.find(c => c.id === displayCategory)?.name ?? '') + ' do bukietu'
                : ''}
            </span>
            <button
              onClick={() => setActiveCategory(null)}
              style={{
                fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                fontWeight: 600, color: MUTED,
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4, padding: 0,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}
            >
              Zamknij <ChevronRight size={13} strokeWidth={2} style={{ transform: 'rotate(-90deg)' }} />
            </button>
          </div>

          {/* Products */}
          <div style={{ padding: '12px' }}>
            {activeDodatki.length === 0 ? (
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, textAlign: 'center', padding: '20px 0', margin: 0 }}>
                Brak dostępnych produktów w tej kategorii
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {activeDodatki.map(d => (
                  <DodatekProductCard
                    key={d.id}
                    d={d}
                    added={selectedDodatki.has(d.id)}
                    onAdd={() => onToggleDodatek(d.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─── Kreator CTA ────────────────────────────────────────────── */
function KreatorCTA() {
  return null;
}

/* ─── Description section ────────────────────────────────────── */
const DESC_FEATURES = [
  { Icon: Flower2,           title: 'Świeże kwiaty',   sub: 'najwyższa jakość' },
  { Icon: Sparkles,          title: 'Ręczna praca',     sub: 'unikatowe kompozycje' },
  { Icon: Truck,             title: 'Dostawa na dziś',  sub: 'zamów do 18:00' },
  { Icon: MessageCircleHeart,title: 'Dołącz bilecik',   sub: 'z Twoimi życzeniami' },
] as const;

function DescriptionSection({ descriptionHtml, collapsible }: { descriptionHtml: string; collapsible?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  if (!descriptionHtml || !descriptionHtml.trim()) return null;

  const clamped = collapsible && !expanded;

  return (
    <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 24, paddingTop: 20 }}>
      {/* Heading */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%',
          background: 'rgba(210,60,120,0.10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Flower2 size={14} color={GOLD} strokeWidth={1.6} />
        </div>
        <h3 style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: 18, fontWeight: 400, color: DARK, margin: 0, letterSpacing: '-0.01em',
        }}>
          O kompozycji
        </h3>
      </div>

      {/* Description text */}
      <div
        className="product-description"
        style={clamped ? { maxHeight: '6.4em', overflow: 'hidden' } : undefined}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(descriptionHtml) }}
      />
      {collapsible && (
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            marginTop: 8, padding: 0, background: 'none', border: 'none',
            fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600,
            color: GOLD, cursor: 'pointer', textDecoration: 'underline',
            textUnderlineOffset: 3,
          }}
        >
          {expanded ? 'Zwiń opis' : 'Rozwiń opis'}
        </button>
      )}
    </div>
  );
}

/* ─── Why Bukietowa ──────────────────────────────────────────── */
function WhyBukietowa() {
  const { ref, inView } = useInView<HTMLElement>('-60px 0px');

  return (
    <>

      <section
        ref={ref}
        style={{
          background: '#fff',
          position: 'relative',
          padding: 'clamp(32px, 5vw, 64px) 0',
        }}
      >

      <div className="page-container">

        {/* Header */}
        <div
          style={{ textAlign: 'center', marginBottom: 'clamp(40px, 6vw, 64px)', opacity: inView ? undefined : 0 }}
          className={inView ? 'bk-fade-up' : undefined}
        >
          {/* Eyebrow */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 20 }}>
            <span style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.38em',
              textTransform: 'uppercase', color: GOLD,
            }}>
              Nasza obietnica
            </span>
          </div>

          <h2 style={{
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(2rem, 3.5vw, 3.2rem)',
            fontWeight: 400, lineHeight: 1.1,
            color: DARK,
            margin: 0,
          }}>
            Dlaczego <em style={{ color: GOLD, fontStyle: 'italic' }}>Bukietowa?</em>
          </h2>
        </div>

        {/* Cards grid — separated by 1px gold lines */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
          gap: 1,
          background: `${GOLD}22`,
        }}>
          {WHY_ITEMS.map((w, i) => (
            <div
              key={w.title}
              style={{
                background: '#fff',
                padding: 'clamp(28px, 3.5vw, 48px) clamp(24px, 3vw, 40px)',
                position: 'relative',
                overflow: 'hidden',
                opacity: inView ? undefined : 0,
                animationDelay: `${0.15 + i * 0.1}s`,
              }}
              className={inView ? 'bk-fade-up' : undefined}
            >


              {/* Icon */}
              <div style={{
                width: 44, height: 44,
                borderRadius: '50%',
                background: `${GOLD}16`,
                border: `1px solid ${GOLD}33`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 22,
              }}>
                {w.icon}
              </div>

              {/* Title */}
              <h3 style={{
                fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 'clamp(1.1rem, 1.6vw, 1.35rem)',
                fontWeight: 500,
                lineHeight: 1.25,
                color: DARK,
                margin: '0 0 12px',
              }}>
                {w.title}
              </h3>

              {/* Description */}
              <p style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 'clamp(13px, 1.1vw, 14px)',
                fontWeight: 300,
                lineHeight: 1.7,
                color: MUTED,
                margin: 0,
              }}>
                {w.desc}
              </p>
            </div>
          ))}
        </div>

      </div>

      </section>

    </>
  );
}

/* ─── Reviews ────────────────────────────────────────────────── */
function ReviewAvatar({ url, name }: { url: string | null; name: string }) {
  const initials = name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  if (url) return (
    <img src={url} alt={name} style={{
      width: 44, height: 44, borderRadius: '50%', objectFit: 'cover',
      border: `2px solid ${LINE}`, flexShrink: 0,
    }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
  );
  return (
    <div style={{
      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
      background: 'rgba(210,60,120,0.1)', border: `2px solid ${LINE}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, color: GOLD,
    }}>{initials}</div>
  );
}

const GOOGLE_G = (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

function ReviewsSection() {
  const [data, setData]       = useState<ReviewsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [atStart, setAtStart] = useState(true);
  const [atEnd,   setAtEnd]   = useState(false);
  const sliderRef             = useRef<HTMLDivElement>(null);
  const isMobile              = useIsMobile();

  useEffect(() => {
    fetch(`${BASE_URL}/api/reviews/google`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((d: ReviewsData) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const reviews  = (data?.source === 'google' ? data.reviews : []).slice(0, 10);
  const rating   = googleRating(data);

  const updateBounds = () => {
    const el = sliderRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  const slide = (dir: -1 | 1) => {
    const el = sliderRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' });
  };

  const navBtn = (dir: -1 | 1, disabled: boolean) => (
    <button
      onClick={() => slide(dir)}
      disabled={disabled}
      aria-label={dir === -1 ? 'Poprzednie opinie' : 'Następne opinie'}
      style={{
        width: 40, height: 40, borderRadius: '50%',
        border: `1px solid ${disabled ? LINE : GOLD}`,
        background: 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: disabled ? 0.35 : 1, transition: 'background 0.2s', flexShrink: 0,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = GOLD_LIGHT; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {dir === -1 ? <ChevronLeft size={16} color={DARK} /> : <ChevronRight size={16} color={DARK} />}
    </button>
  );

  return (
    <div style={{ background: '#fff' }}>
    <div id="opinie-klientow" className="page-container" style={{ paddingTop: isMobile ? 32 : 64, paddingBottom: isMobile ? 32 : 72 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 36, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.26em', textTransform: 'uppercase', color: GOLD, marginBottom: 8 }}>
            Co mówią klienci
          </p>
          <h2 style={{ ...sectionHeading, fontSize: 'clamp(22px, 3vw, 30px)', marginBottom: 10 }}>Opinie klientów</h2>
          {rating && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 2 }}>
              {[1,2,3,4,5].map(n => <Star key={n} size={14} color={GOLD} fill={n <= Math.round(rating.rating) ? GOLD : 'transparent'} />)}
            </div>
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, fontWeight: 500, color: DARK, lineHeight: 1 }}>{rating.score}</span>
            <span style={{ width: 1, height: 16, background: LINE, display: 'block' }} />
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED }}>
              {rating.total.toLocaleString('pl-PL')} opinii w Google
            </span>
          </div>}
        </div>

        <a
          href="https://www.google.com/maps/search/Bukietowa+Kwiaciarnia+Lublin"
          target="_blank" rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontFamily: 'DM Sans, sans-serif', fontSize: 10,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            color: DARK, textDecoration: 'none',
            border: `1px solid ${GOLD}`, padding: '10px 20px',
            transition: 'border-color 0.2s, background 0.2s',
            ...REVIEW_BUTTON_STYLE,
          }}
          onMouseEnter={e => { const el = e.currentTarget; el.style.borderColor = GOLD; el.style.background = GOLD_LIGHT; }}
          onMouseLeave={e => { const el = e.currentTarget; el.style.borderColor = GOLD; el.style.background = 'transparent'; }}
        >
          {GOOGLE_G} Wszystkie opinie
        </a>
      </div>

      {/* ── Slider ── */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Loader2 size={24} color={GOLD} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : reviews.length === 0 ? null : (
        <>
          {/* Hide scrollbar via inline style injection */}
          <style>{`.reviews-slider::-webkit-scrollbar{display:none}`}</style>

          <div
            ref={sliderRef}
            className="reviews-slider"
            onScroll={updateBounds}
            style={{
              display: 'flex', gap: 16,
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            } as React.CSSProperties}
          >
            {reviews.map((r, i) => (
              <div key={i} style={{
                flex: isMobile ? '0 0 100%' : '0 0 calc((100% - 32px) / 3)',
                scrollSnapAlign: 'start',
                background: '#fff',
                border: `1px solid ${GOLD}`,
                borderTop: `2px solid ${GOLD}`,
                padding: '24px 26px',
                display: 'flex', flexDirection: 'column',
                position: 'relative', overflow: 'hidden',
                ...REVIEW_CARD_STYLE,
              }}>
                <span aria-hidden style={{
                  position: 'absolute', top: -8, right: 14,
                  fontFamily: 'Cormorant Garamond, Georgia, serif',
                  fontSize: 80, lineHeight: 1, color: `${GOLD}0e`,
                  userSelect: 'none', pointerEvents: 'none',
                }}>❝</span>

                <div style={{ display: 'flex', gap: 2, marginBottom: 14 }}>
                  {[1,2,3,4,5].map(n => (
                    <Star key={n} size={13} color={GOLD} fill={n <= r.rating ? GOLD : 'transparent'} />
                  ))}
                </div>

                <p style={{
                  fontFamily: 'Cormorant Garamond, Georgia, serif',
                  fontSize: 16, fontStyle: 'italic', fontWeight: 400,
                  color: '#000000',
                  margin: '0 0 20px',
                  ...REVIEW_TEXT_STYLE,
                }}>
                  „{displayText(r.text)}"
                </p>

                <div style={{ height: 1, background: LINE, marginTop: 'auto', marginBottom: 16, flexShrink: 0 }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <ReviewAvatar url={r.avatarUrl} name={r.author} />
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, color: DARK, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.author}
                    </p>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: MUTED, margin: '2px 0 0' }}>
                      {r.relativeTime}
                    </p>
                  </div>
                  <span style={{ marginLeft: 'auto', opacity: 0.55, flexShrink: 0 }}>{GOOGLE_G}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation arrows */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 28 }}>
            {navBtn(-1, atStart)}
            {navBtn( 1, atEnd)}
          </div>
        </>
      )}
    </div>
    </div>
  );
}

/* ─── Related products ───────────────────────────────────────── */
function RelatedProducts({ currentHandle }: { currentHandle: string }) {
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [cardWidth, setCardWidth] = useState(320);
  const [atStart, setAtStart] = useState(true);
  const [atEnd,   setAtEnd]   = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const GAP = 20;
  const CARD_W = cardWidth;

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      // On mobile show ~75vw card so next card peeks; cap at 320
      setCardWidth(w < 768 ? Math.min(320, Math.round(w * 0.75)) : 320);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    fetchProducts(12).then(all => {
      setProducts(
        all
          .filter(p => p.handle !== currentHandle && !isAddonProduct(p))
          .slice(0, 8),
      );
    }).catch(() => {});
  }, [currentHandle]);

  if (!products.length) return null;

  // Kontener przewija się natywnie (overflowX: auto) — działa od razu z
  // przeciąganiem palcem/trackpadem, przyciski wołają tylko scrollBy() na
  // tym samym elemencie zamiast osobno śledzonego transform/offset.
  const updateBounds = () => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  const slide = (dir: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  const ArrowBtn = ({ dir, disabled, onClick }: { dir: 'left' | 'right'; disabled: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === 'left' ? 'Poprzednie produkty' : 'Następne produkty'}
      style={{
        width: 44, height: 44, borderRadius: '50%',
        border: `1px solid ${GOLD}`, background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'default' : 'pointer', flexShrink: 0, transition: 'background 0.18s, opacity 0.18s',
        opacity: disabled ? 0.35 : 1,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = GOLD_LIGHT; }}
      onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
    >
      {dir === 'left'
        ? <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke={DARK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        : <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3L11 8L6 13" stroke={DARK} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      }
    </button>
  );

  return (
    <div style={{ background: '#fff', padding: isMobile ? '32px 0' : '64px 0' }}>
      <div className="page-container">
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
          <h2 style={{ ...sectionHeading, fontSize: 'clamp(22px, 3vw, 32px)', margin: 0 }}>
            Może spodoba Ci się również
          </h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <ArrowBtn dir="left" disabled={atStart} onClick={() => slide(-1)} />
            <ArrowBtn dir="right" disabled={atEnd} onClick={() => slide(1)} />
          </div>
        </div>

        {/* Slider — natywny scroll, działa z przeciąganiem palcem */}
        <div
          ref={trackRef}
          onScroll={updateBounds}
          className="no-scrollbar"
          style={{
            display: 'flex',
            gap: GAP,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: 'x proximity',
            paddingBottom: 2,
          }}
        >
          {products.map(p => (
              <div
                key={p.handle}
                className="bk-labeled-product"
                style={{ position: 'relative', flexShrink: 0, width: CARD_W, display: 'block', scrollSnapAlign: 'start' }}
              >
                <div style={{
                  background: '#fff',
                  border: `1px solid ${LINE}`,
                  transition: 'box-shadow 0.2s ease',
                }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 6px 24px rgba(42,31,20,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
                >
                  <div style={{ position: 'relative' }}>
                  <Link href={productUrl(p.handle)} aria-label={p.title} style={{ textDecoration: 'none', display: 'block' }}>
                  <div style={{ aspectRatio: '4/5', overflow: 'hidden', background: CREAM2 }}>
                    {p.featuredImage ? (
                      <img
                        src={p.featuredImage.url}
                        alt={p.featuredImage.altText ?? p.title}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', transition: 'transform 0.5s ease' }}
                        onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.05)')}
                        onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                      />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: CREAM2 }} />
                    )}
                  </div>
                  </Link>
                  <ProductLabelOverlay product={p} />
                  </div>
                  <Link href={productUrl(p.handle)} style={{ textDecoration: 'none', display: 'block' }}>
                  <div style={{ padding: '16px 20px' }}>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: DARK, margin: '0 0 4px', lineHeight: 1.3 }}>
                      {p.title}
                    </p>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: GOLD, margin: 0 }}>
                      {productDisplayPrice(p.priceRange)}
                    </p>
                  </div>
                  </Link>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Shared styles ──────────────────────────────────────────── */
const sectionHeading: React.CSSProperties = {
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: 24, fontWeight: 400,
  color: DARK, marginBottom: 20,
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '28px 0 18px' }}>
      <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: DARK, fontStyle: 'italic' }}>
        {children}
      </span>
      <div style={{ flex: 1, height: 1, background: LINE }} />
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */
export default function ProductPage() {
  const isMobile = useIsMobile();
  const params = useParams<{ handle: string }>();
  const handle = params.handle ?? '';

  const [product,  setProduct]  = useState<ShopifyProductFull | null>(() => getCachedProduct(handle) ?? null);
  const [loading,  setLoading]  = useState(() => !getCachedProduct(handle));
  const [notFound, setNotFound] = useState(false);
  usePrerenderReady(!loading, notFound ? 404 : 200);

  // Ta strona montuje własny, sticky pasek CTA na dole ekranu (mobile) —
  // dopóki jest widoczny, każe MobileBottomNav się odmontować, żeby nigdy
  // nie istniały jednocześnie dwa niezależne position:fixed paski
  // przyklejone do tej samej krawędzi ekranu (patrz mobilePanels.ts).
  // Włączamy dopiero gdy produkt faktycznie się załadował — to dokładnie
  // ten sam warunek co przy renderze samego paska CTA niżej.
  useEffect(() => {
    if (!isMobile || loading || !product) return;
    setBottomNavHidden(true);
    return () => setBottomNavHidden(false);
  }, [isMobile, loading, product]);

  const productSeoDescription = product
    ? stripHtml(`${product.title}. ${product.descriptionHtml || product.description || 'Zamów z dostawą na terenie Lublina i okolic.'}`)
    : undefined;

  useSeo({
    title: product?.title ?? handle,
    description: productSeoDescription,
    ogImage: product?.images.nodes[0]?.url,
    ogType: 'website',
  });

  // Detect navigation context — module-level flag set by CollectionProductCard / BukietyColumn
  const [collectionCtx] = useState(() => consumeCollectionContext());

  // JSON-LD: Product + BreadcrumbList — structured data dla wyników Google.
  // Celowo BEZ aggregateRating/review: dane w StarRating/ReviewsSection to
  // opinie Google Business Profile całego sklepu, nie recenzje tego
  // konkretnego produktu — użycie ich w Product naruszałoby wytyczne Google
  // (ryzyko manual action / utraty rich results zamiast ich zyskania).
  const productJsonLd = useMemo(() => {
    if (!product) return null;
    const prices = product.variants.nodes
      .map(v => parseFloat(v.price.amount))
      .filter(n => Number.isFinite(n));
    if (prices.length === 0) return null;

    const currency      = product.variants.nodes[0]!.price.currencyCode;
    const inStock       = !productUnavailableReason(product) && product.variants.nodes.some(v => v.availableForSale);
    const url           = `https://bukietowa.pl${productUrl(product.handle)}`;
    const lowPrice       = Math.min(...prices);
    const highPrice      = Math.max(...prices);
    const availability   = inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

    return {
      '@context': 'https://schema.org',
      '@type':    'Product',
      name:        product.title,
      description: productSeoDescription ?? product.title,
      image:       product.images.nodes.map(i => i.url),
      sku:         product.id,
      url,
      brand: { '@type': 'Brand', name: 'Bukietowa' },
      offers: lowPrice === highPrice
        ? { '@type': 'Offer', priceCurrency: currency, price: lowPrice, availability, url }
        : { '@type': 'AggregateOffer', priceCurrency: currency, lowPrice, highPrice, offerCount: prices.length, availability, url },
    };
  }, [product]);

  const breadcrumbParent = productBreadcrumbParent(product?.collections?.nodes);
  const parentHandle = handleFromPublicUrl(breadcrumbParent.href);
  // Match the category landing pages: funeral and other flower categories
  // have a parent, while Flower Boxy is a standalone landing page.
  const breadcrumbAncestor = breadcrumbParent.href.startsWith('/kompozycje-pogrzebowe/')
    ? { label: 'Kompozycje pogrzebowe', href: '/kompozycje-pogrzebowe/' }
    : parentHandle && breadcrumbParent.href !== '/flower-boxy/'
      ? findBreadcrumbParent(parentHandle)
      : null;
  const breadcrumbCrumbs = [
    ...(breadcrumbAncestor ? [breadcrumbAncestor] : []),
    breadcrumbParent,
    { label: notFound ? 'Nie znaleziono produktu' : product?.title ?? 'Produkt' },
  ];

  // Funeral category: replace "Bilecik" with "Szarfa" everywhere on the product card
  const isFuneralCategory = (
    (collectionCtx?.href ?? '').toLowerCase().includes('pogrzeb') ||
    (collectionCtx?.label ?? '').toLowerCase().includes('pogrzeb') ||
    (product?.tags ?? []).some(t => t.toLowerCase().includes('pogrzeb'))
  );

  // Doniczkowe category: hide size selector, auto-select variant, renumber steps
  const isDoniczkoweCategory = (
    (collectionCtx?.href ?? '').toLowerCase().includes('doniczkowe') ||
    (collectionCtx?.label ?? '').toLowerCase().includes('doniczkowe') ||
    (product?.tags ?? []).some(t => t.toLowerCase().includes('doniczkowe'))
  );

  // Sztuczne kompozycje: always hide Rozmiar variant, auto-select first variant
  const isSztuczneKompozycjeCategory = (
    (collectionCtx?.href ?? '').toLowerCase().includes('sztuczne-kompozycje') ||
    (collectionCtx?.label ?? '').toLowerCase().includes('sztuczne') ||
    (product?.tags ?? []).some(t => t.toLowerCase().includes('sztuczne-kompozycje'))
  );

  // True when there is only one "Default Title" variant — size step hidden entirely
  const hasOnlyDefaultVariant = !product || (
    product.variants.nodes.length === 1 && product.variants.nodes[0].title === 'Default Title'
  );
  const isDoniczkoweSizeHidden = isDoniczkoweCategory && hasOnlyDefaultVariant;

  // stepN: shifts base step number down by 1 when the size step is hidden
  const stepN = (base: number) => (isDoniczkoweSizeHidden || isSztuczneKompozycjeCategory) ? base - 1 : base;

  const [selected, setSelected] = useState<ShopifyVariantFull | null>(() =>
    selectInitialVariant(product, collectionCtx),
  );
  useGoogleAnalyticsEvent('view_item', selected ? analyticsItems([{ variantId: selected.id, price: selected.price.amount, quantity: 1 }]) : [], !loading && product ? product.id : '');
  const [added,    setAdded]    = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const unavailableReason = productUnavailableReason(product, selected ?? { availableForSale: false });
  const canAddToCart = Boolean(selected?.availableForSale) && !unavailableReason && !isAdding;

  // Additki by category, fetched live from Shopify with fallbacks
  const [dodatkiByCategory, setDodatkiByCategory] = useState<Record<string, DodatekResolved[]>>(
    initialAddonsByCategory
  );

  // Personalization state
  const [note,            setNote]            = useState('');
  const [selectedDodatki, setSelectedDodatki] = useState<Set<string>>(new Set());

  // The card and funeral ribbon use the same inline editor.
  const [bilecikDraft, setBilecikDraft] = useState('');

  // Recipient state and the combined recipient/card form drafts
  const [recipientName,       setRecipientName]       = useState('');
  const [recipientPhone,      setRecipientPhone]      = useState('');
  const [recipientNameDraft,  setRecipientNameDraft]  = useState('');
  const [recipientPhoneDraft, setRecipientPhoneDraft] = useState('');
  const [personalizationEditing, setPersonalizationEditing] = useState(true);
  const [recipientFieldErrors, setRecipientFieldErrors] = useState({ name: false, phone: false });
  const [submitAttempted,     setSubmitAttempted]     = useState(false);

  // Delivery form state
  const [deliveryDetails, setDeliveryDetails] = useState<SavedDelivery | null>(null);
  const [deliveryConfirmed, setDeliveryConfirmed] = useState(false);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveryModalMode, setDeliveryModalMode] = useState<'delivery' | 'pickup'>('delivery');
  const [deliveryCurrentMode, setDeliveryCurrentMode] = useState<'delivery' | 'pickup'>('delivery');
  const closeDeliveryModal = useCallback(() => setDeliveryModalOpen(false), []);
  const deliveryDialogRef = useFocusTrap<HTMLDivElement>(deliveryModalOpen, closeDeliveryModal);

  const { addBatch, items: cartItems } = useCart();

  useScrollLock(deliveryModalOpen);


  useEffect(() => {
    fetchAddonCatalog().then(setDodatkiByCategory);
  }, []);

  useEffect(() => {
    if (!handle) return;
    const hasSeededProduct = Boolean(getCachedProduct(handle));
    if (!hasSeededProduct) setLoading(true);
    setNotFound(false);
    fetchProductByHandle(handle)
      .then(p => {
        if (!p) { setNotFound(true); return; }
        // Block addon products — they have no standalone browsable page
        const productCollectionKeys = (p.collections?.nodes ?? [])
          .flatMap(c => [c.handle, c.title ?? ''])
          .map(value => value.trim().toLocaleLowerCase('pl-PL'))
          .filter(Boolean);
        const isAddon = productCollectionKeys.some(key => ADDON_COLLECTION_KEYS.has(key));
        if (isAddon) { setNotFound(true); return; }
        if (isPrerenderMode) {
          (globalThis as typeof globalThis & { __BUKIETOWA_PRERENDER_PRODUCT__?: ShopifyProductFull })
            .__BUKIETOWA_PRERENDER_PRODUCT__ = p;
        }
        setProduct(p);
        const nodes = p.variants.nodes;
        // Detect doniczkowe using both collectionCtx and fresh product tags
        const doniczkowe = (
          (collectionCtx?.href ?? '').toLowerCase().includes('doniczkowe') ||
          (collectionCtx?.label ?? '').toLowerCase().includes('doniczkowe') ||
          (p.tags ?? []).some((t: string) => t.toLowerCase().includes('doniczkowe'))
        );
        // Detect sztuczne-kompozycje — always auto-select, no size step shown
        const sztuczne = (
          (collectionCtx?.href ?? '').toLowerCase().includes('sztuczne-kompozycje') ||
          (collectionCtx?.label ?? '').toLowerCase().includes('sztuczne') ||
          (p.tags ?? []).some((t: string) => t.toLowerCase().includes('sztuczne-kompozycje'))
        );
        // For doniczkowe/sztuczne-kompozycje: auto-select first available (no "Średni" preference).
        // This guarantees `selected` is always set before the user interacts with the add-to-cart button.
        const variant = (doniczkowe || sztuczne)
          ? (nodes.find(v => v.availableForSale) ?? nodes[0] ?? null)
          : (nodes.find(v => v.title === 'Średni' && v.availableForSale)
              ?? nodes.find(v => v.title === 'Średni')
              ?? nodes.find(v => v.availableForSale)
              ?? nodes[0]
              ?? null);
        setSelected(variant);
      })
      .catch(err => {
        // A transport failure is not a missing product. Keep the existing
        // product state and reserve the 404 view for the null result above.
        console.warn(`[ProductPage] refresh failed for ${handle}:`, err);
      })
      .finally(() => setLoading(false));
  }, [handle]);

  const price = useMemo(() => {
    if (!selected) return null;
    return formatPrice(selected.price.amount, selected.price.currencyCode);
  }, [selected]);

  /* "od X zł" fallback shown before a variant is selected */
  const headerPrice = useMemo(() => {
    const nodes = product?.variants.nodes ?? [];
    if (nodes.length === 0) return '';
    const prices = nodes.map(v => parseFloat(v.price.amount) || 0);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const minNode = nodes[prices.indexOf(min)];
    const formatted = formatPrice(String(min), minNode.price.currencyCode);
    return max > min ? `od ${formatted}` : formatted;
  }, [product]);

  /* Minimum variant price, formatted — used for the mobile "Od X zł" header */
  const minVariantPrice = useMemo(() => {
    const nodes = product?.variants.nodes ?? [];
    if (nodes.length === 0) return '';
    const prices = nodes.map(v => parseFloat(v.price.amount) || 0);
    const min = Math.min(...prices);
    return `${min.toFixed(2).replace('.', ',')} zł`;
  }, [product]);

  const allDodatki = useMemo(() =>
    Object.values(dodatkiByCategory).flat(),
    [dodatkiByCategory]
  );

  const extraTotal = useMemo(() =>
    allDodatki.filter(d => selectedDodatki.has(d.id)).reduce((s, d) => s + d.price, 0),
    [selectedDodatki, allDodatki]
  );

  const grandTotal = useMemo(() => {
    if (!selected) return 0;
    const base = parseFloat(selected.price.amount) || 0;
    const delivery = deliveryDetails?.cost.cost ?? 0;
    return base + extraTotal + delivery;
  }, [selected, extraTotal, deliveryDetails]);

  const toggleDodatek = useCallback((id: string) => {
    setSelectedDodatki(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleAddToCart = async () => {
    if (!selected || !product || !canAddToCart) return;

    // Validate required fields — show inline errors instead of disabling
    // Funeral products don't require recipient data
    if (!deliveryConfirmed || (!isFuneralCategory && !recipientName.trim())) {
      setSubmitAttempted(true);
      return;
    }

    // Attributes for the main product (bilecik + additki without Shopify variant)
    const attributes: { key: string; value: string }[] = [];
    // An optional ribbon typed inline must survive adding the product before Save.
    const orderNote = isFuneralCategory ? bilecikDraft.trim() : note.trim();
    if (isFuneralCategory)     attributes.push({ key: '_pogrzebowy',       value: 'true' });
    if (orderNote)             attributes.push({ key: isFuneralCategory ? 'Szarfa' : 'Bilecik', value: orderNote });
    if (recipientName.trim())  attributes.push({ key: 'Imię odbiorcy',    value: recipientName.trim() });
    if (recipientPhone.trim()) attributes.push({ key: 'Telefon odbiorcy', value: recipientPhone.trim() });

    const selectedList = allDodatki.filter(d => selectedDodatki.has(d.id));
    const textOnlyDodatki = selectedList.filter(d => !d.shopifyVariantId);
    if (textOnlyDodatki.length) {
      attributes.push({ key: 'Dodatki', value: textOnlyDodatki.map(d => d.name).join(', ') });
    }

    // Build the batch: bouquet first, then Shopify-backed addons
    const shopifyDodatki = selectedList.filter(d => d.shopifyVariantId);
    setIsAdding(true);
    const wasAdded = await addBatch([
      {
        variantId: selected.id,
        variantTitle: selected.title === 'Default Title' ? product.title : selected.title,
        productTitle: product.title,
        price: price ?? '',
        unitAmountGrosze: priceAmountGrosze(selected.price.amount) ?? undefined,
        image: product.images.nodes[0]?.url ?? null,
        attributes,
        delivery: deliveryDetails ?? undefined,
      },
      ...shopifyDodatki.map(d => ({
        addonId: d.id,
        variantId: d.shopifyVariantId!,
        variantTitle: d.name,
        productTitle: d.name,
        price: formatPrice(String(d.price), 'PLN'),
        unitAmountGrosze: priceAmountGrosze(d.price) ?? undefined,
        image: d.imageUrl ?? null,
        attributes: [{ key: 'Do zamówienia', value: product.title }],
        delivery: deliveryDetails ?? undefined,
      })),
    ], textOnlyDodatki.map(d => ({
      addonId: d.id,
      variantId: `addon-preview:${d.id}`,
      variantTitle: d.name,
      productTitle: d.name,
      price: formatPrice(String(d.price), 'PLN'),
      unitAmountGrosze: priceAmountGrosze(d.price) ?? undefined,
      image: d.imageUrl ?? null,
      attributes: [{ key: 'Do zamówienia', value: product.title }],
      delivery: deliveryDetails ?? undefined,
    })));

    setIsAdding(false);
    if (!wasAdded) return;
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);

    // Reset form to defaults so next visit starts clean
    setNote('');
    setBilecikDraft('');
    setRecipientName('');
    setRecipientPhone('');
    setRecipientNameDraft('');
    setRecipientPhoneDraft('');
    setPersonalizationEditing(true);
    setDeliveryDetails(null);
    setDeliveryConfirmed(false);
    setSelectedDodatki(new Set());
    setSubmitAttempted(false);
  };

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh' }}>
      {productJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeCmsJson(productJsonLd) }} />
      )}
      <PageBreadcrumb overlay={false} crumbs={breadcrumbCrumbs} />
      {/* ── Loading ── */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 500, gap: 12, color: MUTED, fontFamily: 'DM Sans, sans-serif', fontSize: 13 }}>
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
          Ładowanie produktu…
        </div>
      )}
      {/* ── Not found ── */}
      {!loading && notFound && (
        <div style={{ textAlign: 'center', padding: '100px 24px' }}>
          <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 34, color: DARK, marginBottom: 16 }}>
            Nie znaleziono produktu
          </p>
          <Link href="/" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: GOLD, textDecoration: 'none', letterSpacing: '0.08em' }}>
            Wróć na stronę główną →
          </Link>
        </div>
      )}
      {/* ── Product ── */}
      {!loading && product && (
        <>
          <div style={{ maxWidth: 1370, margin: '0 auto', padding: `28px var(--page-gutter) ${isMobile ? '24px' : '0'}` }}>

            {/* ← Wróć do [kolekcja] — desktop only.
                Ukrywane przez CSS (`hidden md:block`), NIE przez `{!isMobile && ...}` —
                warunek JS zależy od stanu policzonego dopiero po hydracji, więc na
                niezhydrowanym prerenderowanym HTML-u (gdzie `isMobile` domyślnie = false,
                bo `window` nie istnieje przy renderze na serwerze) ten link migał na
                mobile zanim React zdążył przeliczyć realną szerokość ekranu. */}
            <div className="hidden md:block" style={{ marginBottom: 16 }}>
              <Link
                href={collectionCtx?.href ?? '/sklep/'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontFamily: 'DM Sans, sans-serif', fontSize: 12,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  color: MUTED, textDecoration: 'none',
                  transition: 'color 0.18s',
                }}
                onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.color = GOLD)}
                onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => (e.currentTarget.style.color = MUTED)}
              >
                <ArrowLeft size={13} strokeWidth={1.8} />
                Wróć do {collectionCtx?.label ?? 'Sklep'}
              </Link>
            </div>

            {/* Two-column layout — single column on mobile */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 3fr) minmax(0, 2fr)',
              gap: isMobile ? 32 : 'clamp(32px, 4vw, 64px)',
              alignItems: 'stretch',
              marginBottom: isMobile ? 0 : 64,
            }}>
              {/* RIGHT — Details. Placed FIRST in the DOM (not visually) so the product h1
                  below comes before the gallery's "O kompozycji" h3 in source order —
                  CSS `order` puts it back on the right visually on desktop, and BELOW the
                  gallery on mobile's single-column grid (order applies regardless of column
                  count — without it here, mobile fell back to DOM order and showed the
                  details/config block above the product photo instead of below it). */}
              <div id="product-config" style={{ order: 2 }}>
                 {!unavailableReason && <DeliveryBadge />}

                {isMobile ? (
                  /* ── Mobile header: title + rating in one row, "Od" price, description ── */
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                      <h1 style={{
                        fontFamily: 'DM Sans, sans-serif', fontSize: 22, fontWeight: 700,
                        color: DARK, lineHeight: 1.2, margin: 0,
                        flex: 1, minWidth: 0,
                      }}>
                        {product.title}
                      </h1>
                      <div style={{ paddingTop: 4, flexShrink: 0 }}><StarRating compact /></div>
                    </div>
                    <p style={{
                      fontFamily: 'DM Sans, sans-serif', fontSize: 22, fontWeight: 700,
                      color: GOLD, lineHeight: 1.2, margin: '0 0 10px',
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: DARK, marginRight: 2 }}>Od</span>
                      {minVariantPrice}
                    </p>
                    {parseShortDescription(product.shortDescription?.value) && (
                      <p style={{
                        fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 400,
                        color: 'rgba(42,31,20,0.7)', lineHeight: 1.6,
                        margin: '0 0 18px',
                      }}>
                        {parseShortDescription(product.shortDescription?.value)}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    {/* Title */}
                    <h1 style={{
                      fontFamily: 'Cormorant Garamond, serif',
                      fontSize: 'clamp(28px, 3.5vw, 48px)',
                      fontWeight: 400, color: DARK,
                      lineHeight: 1.12, marginBottom: 10,
                    }}>
                      {product.title}
                    </h1>

                    {/* Short description — visible only on product page */}
                    {parseShortDescription(product.shortDescription?.value) && (
                      <p style={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: 'clamp(13px, 1.3vw, 15px)',
                        fontWeight: 300,
                        color: 'rgba(42,31,20,0.62)',
                        lineHeight: 1.65,
                        margin: '0 0 12px',
                        letterSpacing: '0.01em',
                      }}>
                        {parseShortDescription(product.shortDescription?.value)}
                      </p>
                    )}

                    {/* Rating */}
                    <StarRating />

                    {/* Price */}
                    <p style={{
                      fontFamily: 'Cormorant Garamond, serif',
                      fontSize: 'clamp(22px, 2.5vw, 30px)',
                      fontWeight: 500,
                      color: DARK,
                      lineHeight: 1.2,
                      margin: '10px 0 18px',
                    }}>
                      {price ?? headerPrice}
                    </p>
                  </>
                )}

                <ProductLabels product={product} interactive unavailableReason={unavailableReason} style={{ marginBottom: 12 }} />
                <ProductUnavailableNotice reason={unavailableReason} id="product-unavailable-reason" />

                {/* Step 1: size selector — hidden entirely for doniczkowe with single Default Title variant.
                    For doniczkowe with multiple non-default variants, VariantSelector renders simple
                    tiles (no bouquet icons) because the option name won't match isSizeOption. */}
                {!isDoniczkoweSizeHidden && !isSztuczneKompozycjeCategory && (
                  isMobile ? (
                    <div style={{ marginBottom: 20 }}>
                      <p style={{
                        fontFamily: 'DM Sans, sans-serif', fontSize: 14, fontWeight: 600,
                        color: DARK, margin: '0 0 12px',
                      }}>
                        1. Wybierz rozmiar bukietu
                      </p>
                      <VariantSelector variants={product.variants.nodes} selected={selected} onSelect={setSelected} />
                    </div>
                  ) : (
                    <StepRow n={1} title="Wybierz rozmiar" completed={!!selected}>
                      <VariantSelector variants={product.variants.nodes} selected={selected} onSelect={setSelected} />
                    </StepRow>
                  )
                )}

                <StepShell n={stepN(2)} title="Dostawa / odbiór osobisty" mobileTitle="Wybierz dostawę lub odbiór" icon={<Truck size={19} color={DARK} strokeWidth={1.6} />} completed={deliveryConfirmed} error={submitAttempted && !deliveryConfirmed}>
                <div>
                  {deliveryConfirmed && deliveryDetails ? (
                    /* ── Confirmed summary ── */
                    (<div style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: '12px 16px',
                      border: '1.5px solid rgba(44,85,48,0.3)',
                      background: 'rgba(44,85,48,0.04)',
                      borderRadius: 'var(--config-card-radius)',
                    }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        background: GREEN,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, marginTop: 1,
                      }}>
                        <Check size={12} color="#fff" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {deliveryDetails.cost.cost === 0 ? (
                          <>
                            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(42,31,20,0.45)', margin: '0 0 2px' }}>
                              Odbiór osobisty
                            </p>
                            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, textTransform: 'uppercase', color: DARK, margin: '0 0 2px' }}>
                              {deliveryDetails.details.origin === 'bursztynowa'
                                ? 'ul. Bursztynowa 17/4'
                                : 'al. Warszawska 23/1'}
                            </p>
                            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK, margin: '0 0 2px' }}>
                              {deliveryDetails.details.date} - {formatPickupSlot(deliveryDetails.details.slot)}
                            </p>
                          </>
                        ) : (
                          <>
                            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(42,31,20,0.45)', margin: '0 0 2px' }}>
                              🚗 Dostawa
                            </p>
                            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK, margin: '0 0 2px' }}>
                              {deliveryDetails.details.destination}
                              {!deliveryDetails.details.noApartment && deliveryDetails.details.apartmentNumber && ` m. ${deliveryDetails.details.apartmentNumber}`}
                            </p>
                            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'rgba(42,31,20,0.45)', margin: 0 }}>
                              {deliveryDetails.details.date} · {deliveryDetails.cost.cost.toFixed(2).replace('.', ',')} zł
                            </p>
                          </>
                        )}
                      </div>
                      <button
                         onClick={() => {
                           setDeliveryCurrentMode(deliveryDetails.cost.cost === 0 ? 'pickup' : 'delivery');
                           setDeliveryModalOpen(true);
                         }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          background: 'none', border: `1px solid ${GOLD}`,
                          padding: '8px 12px', cursor: 'pointer',
                          fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                           color: MUTED, flexShrink: 0, minHeight: 36,
                           borderRadius: 'var(--config-field-radius)',
                        }}
                      >
                        Zmień
                      </button>
                    </div>)
                  ) : (
                    /* ── Two trigger buttons ── */
                    (<div style={{ display: 'flex', gap: 8 }}>
                      {([
                        { mode: 'delivery' as const, icon: <Truck size={14} color={DARK} />, label: 'Dostawa' },
                        { mode: 'pickup'   as const, icon: <Package size={14} color={DARK} />, label: 'Odbiór osobisty' },
                      ]).map(({ mode, icon, label }) => (
                        <button
                          key={mode}
                           onClick={() => {
                             setDeliveryModalMode(mode);
                             setDeliveryCurrentMode(mode);
                             setDeliveryModalOpen(true);
                           }}
                          style={{
                            flex: 1, padding: '13px 12px', cursor: 'pointer', minHeight: 48,
                            border: `1.5px solid ${LINE}`,
                            background: '#fff',
                            borderRadius: 'var(--config-card-radius)',
                            fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                            transition: 'border-color 0.15s, background 0.15s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = GOLD_LIGHT; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = '#fff'; }}
                        >
                          {icon}
                          {label}
                        </button>
                      ))}
                    </div>)
                  )}
                </div>
                </StepShell>

                {/* Delivery modal */}
                {deliveryModalOpen && (
                  <div
                    className="product-delivery-overlay"
                    onPointerDown={event => {
                      if (event.target === event.currentTarget) closeDeliveryModal();
                    }}
                    style={{
                      position: 'fixed', inset: 0,
                      background: 'rgba(10,8,5,0.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 200, padding: '20px 16px',
                    }}
                  >
                    <div
                      ref={deliveryDialogRef}
                      className={`product-delivery-dialog${deliveryCurrentMode === 'pickup' ? ' product-delivery-dialog-pickup' : ''}`}
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="delivery-modal-title"
                      onClick={e => e.stopPropagation()}
                       style={{
                         background: '#fff',
                          width: '100%', maxWidth: deliveryCurrentMode === 'pickup' ? 660 : 960,
                          maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
                        padding: '18px clamp(18px, 2.5vw, 28px) 24px',
                         borderRadius: 'var(--config-modal-radius)',
                         boxShadow: '0 22px 70px rgba(42,31,20,0.22)',
                       }}
                    >
                      <div className="product-delivery-modal-toolbar">
                        <button
                          onClick={closeDeliveryModal}
                          aria-label="Zamknij"
                          className="product-delivery-close"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 44, height: 44, color: MUTED,
                          }}
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <Suspense fallback={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320, gap: 12, color: MUTED, fontFamily: 'DM Sans, sans-serif', fontSize: 13 }}>
                          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                          Wczytywanie formularza…
                        </div>
                      }>
                      <CheckoutDeliveryForm
                        key={deliveryModalMode}
                        groupIndex={0}
                        groupCount={1}
                        cartItems={[]}
                        defaultMode={deliveryModalMode}
                        // Pierwszy wybór trybu dzieje się na zewnątrz (dwa duże przyciski
                        // Dostawa/Odbiór), więc przełącznik w środku byłby zbędny — ale przy
                        // edycji już potwierdzonej dostawy ("Zmień") tych przycisków już nie
                        // ma na ekranie, więc bez przełącznika nie dało się zmienić metody
                        // inaczej niż w checkoucie.
                        hideModeToggle={!deliveryConfirmed}
                        confirmOnMount={false}
                        startEditing={deliveryConfirmed}
                        initialDelivery={deliveryDetails ?? undefined}
                        onModeChange={setDeliveryCurrentMode}
                        funeralMode={isFuneralCategory}
                         presentation="product"
                         heading={deliveryCurrentMode === 'pickup' ? 'Gdzie odebrać?' : 'Gdzie dostarczyć?'}
                         headingId="delivery-modal-title"
                        onConfirm={(details: DeliveryDetails, cost: DeliveryCostResult) => {
                          setDeliveryDetails({ details, cost });
                          setDeliveryConfirmed(true);
                          setDeliveryModalOpen(false);
                        }}
                        onEdit={() => {/* stays open */}}
                        onRecipientFill={(name: string, phone: string) => {
                          if (name) { setRecipientName(name); setRecipientNameDraft(name); }
                          if (phone) { setRecipientPhone(phone); setRecipientPhoneDraft(phone); }
                        }}
                      />
                      </Suspense>
                    </div>
                  </div>
                )}

                  <StepShell
                    n={stepN(3)}
                    title={isFuneralCategory ? 'Szarfa' : 'Dane odbiorcy i bilecik'}
                    mobileTitle={isFuneralCategory ? 'Dodaj szarfę z napisem' : 'Dane odbiorcy i bilecik'}
                    icon={isFuneralCategory ? <MessageCircleHeart size={19} color={DARK} strokeWidth={1.6} /> : <Calendar size={19} color={DARK} strokeWidth={1.6} />}
                    completed={isFuneralCategory ? !!note.trim() : !!recipientName.trim()}
                    error={!isFuneralCategory && submitAttempted && !recipientName.trim()}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: (isFuneralCategory ? note.trim() : recipientName.trim()) && !personalizationEditing ? 'none' : 'contents' }}>
                      {!isFuneralCategory && <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            value={recipientNameDraft}
                            onChange={e => {
                              setRecipientNameDraft(e.target.value);
                              if (recipientFieldErrors.name && e.target.value.trim()) {
                                setRecipientFieldErrors(errors => ({ ...errors, name: false }));
                              }
                            }}
                            placeholder="Imię i nazwisko odbiorcy"
                            aria-label="Imię i nazwisko odbiorcy"
                            aria-invalid={recipientFieldErrors.name}
                            aria-describedby={recipientFieldErrors.name ? 'recipient-name-error' : undefined}
                            style={{
                              width: '100%', padding: '12px 14px',
                              border: `1px solid ${recipientFieldErrors.name ? ERROR_RED : GOLD}`, background: '#fff',
                              borderRadius: 'var(--config-field-radius)',
                              fontFamily: 'DM Sans, sans-serif', fontSize: 14,
                              color: DARK, outline: 'none', boxSizing: 'border-box' as const,
                            }}
                          />
                          {recipientFieldErrors.name && (
                            <p id="recipient-name-error" style={{ color: ERROR_RED, fontFamily: 'DM Sans, sans-serif', fontSize: 12, margin: '5px 0 0' }}>
                              Uzupełnij imię i nazwisko odbiorcy.
                            </p>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <PhoneInput
                            value={recipientPhoneDraft}
                            onChange={phone => {
                              setRecipientPhoneDraft(phone);
                              if (recipientFieldErrors.phone && isValidInternationalPhone(phone)) {
                                setRecipientFieldErrors(errors => ({ ...errors, phone: false }));
                              }
                            }}
                            placeholder="Numer telefonu odbiorcy"
                            prefixAriaLabel="Kod kraju odbiorcy"
                            required
                            error={recipientFieldErrors.phone ? phoneValidationMessage(recipientPhoneDraft, true) ?? "Uzupełnij numer telefonu odbiorcy." : undefined}
                            style={{
                              width: '100%', padding: '12px 14px',
                              border: `1px solid ${recipientFieldErrors.phone ? ERROR_RED : GOLD}`, background: '#fff',
                              borderRadius: 'var(--config-field-radius)',
                              fontFamily: 'DM Sans, sans-serif', fontSize: 14,
                              color: DARK, outline: 'none', boxSizing: 'border-box' as const,
                            }}
                          />
                          {recipientFieldErrors.phone && (
                            <p id="recipient-phone-error" style={{ color: ERROR_RED, fontFamily: 'DM Sans, sans-serif', fontSize: 12, margin: '5px 0 0' }}>
                              {phoneValidationMessage(recipientPhoneDraft, true) ?? "Uzupełnij numer telefonu odbiorcy."}
                            </p>
                          )}
                        </div>
                      </div>}
                      <div style={{ position: 'relative' }}>
                        <textarea
                          value={bilecikDraft}
                          onChange={e => setBilecikDraft(e.target.value.slice(0, 300))}
                          placeholder={isFuneralCategory ? 'Treść szarfy (opcjonalnie), np. W hołdzie i wiecznej pamięci…' : 'Treść bileciku (opcjonalnie), np. Kocham Cię, Mamo! ❤️'}
                          aria-label={isFuneralCategory ? 'Treść szarfy' : 'Treść bileciku'}
                          rows={4}
                          style={{
                            width: '100%', padding: '12px 14px',
                            border: `1px solid ${GOLD}`, background: '#fff',
                            borderRadius: 'var(--config-field-radius)',
                            fontFamily: 'DM Sans, sans-serif', fontSize: 14,
                            color: DARK, lineHeight: 1.6, resize: 'vertical',
                            outline: 'none', boxSizing: 'border-box' as const, minHeight: 100,
                          }}
                        />
                        <span style={{
                          position: 'absolute', bottom: 10, right: 12,
                          fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: MUTED,
                          pointerEvents: 'none',
                        }}>
                          {bilecikDraft.length}/300
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!isFuneralCategory) {
                            const name = recipientNameDraft.trim();
                            const phone = recipientPhoneDraft.trim();
                            const errors = { name: !name, phone: !isValidInternationalPhone(phone) };
                            setRecipientFieldErrors(errors);
                            if (errors.name || errors.phone) return;

                            setRecipientName(name);
                            setRecipientPhone(phone);
                          }
                          setNote(bilecikDraft.trim());
                          setPersonalizationEditing(false);
                        }}
                        style={{ ...productCartCtaStyle(true), width: 'auto', alignSelf: 'flex-end', padding: '0 24px' }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = '#000';
                          e.currentTarget.style.color = GOLD;
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = GOLD;
                          e.currentTarget.style.color = '#fff';
                        }}
                      >
                        Zapisz
                      </button>
                      </div>
                      {(isFuneralCategory ? note.trim() : recipientName.trim()) && !personalizationEditing && (
                        <div style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: '12px 14px',
                          border: '1.5px solid rgba(46,139,71,0.3)',
                          background: 'rgba(46,139,71,0.04)',
                          borderRadius: 'var(--config-card-radius)',
                        }}>
                          <div style={{
                            width: 20, height: 20, borderRadius: '50%',
                            background: GREEN, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, marginTop: 1,
                          }}>
                            <Check size={12} color="#fff" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{
                              fontFamily: 'DM Sans, sans-serif', fontSize: 12,
                              letterSpacing: '0.08em', textTransform: 'uppercase',
                              color: 'rgba(42,31,20,0.55)', margin: '0 0 2px',
                            }}>
                              {isFuneralCategory ? 'Zapisano treść szarfy' : `Zapisano dane odbiorcy${note.trim() ? ' i bilecik' : ''}`}
                            </p>
                            {!isFuneralCategory && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK, margin: 0, lineHeight: 1.5 }}>
                              {recipientName}{recipientPhone ? ` · ${recipientPhone}` : ''}
                            </p>}
                            {note.trim() && (
                              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK, margin: '3px 0 0', lineHeight: 1.5, wordBreak: 'break-word' }}>
                                {note}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => { setBilecikDraft(note); setPersonalizationEditing(true); }}
                            style={{
                              background: 'none', border: `1px solid ${GOLD}`,
                              padding: '8px 12px', cursor: 'pointer',
                              borderRadius: 'var(--config-field-radius)',
                              fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                              letterSpacing: '0.08em', textTransform: 'uppercase',
                              color: DARK, flexShrink: 0, minHeight: 36,
                            }}
                          >
                            Zmień
                          </button>
                        </div>
                      )}
                    </div>
                  </StepShell>

                {/* Last step — Dodatki */}
                {!isFuneralCategory && <StepShell n={stepN(4)} title="Dodatki do zamówienia" mobileTitle="Wybierz dodatki do bukietu" icon={<Gift size={19} color={DARK} strokeWidth={1.6} />} last completed={selectedDodatki.size > 0}>
                  <PersonalizationSection
                    note={note} onNote={setNote}
                    selectedDodatki={selectedDodatki} onToggleDodatek={toggleDodatek}
                    dodatkiByCategory={dodatkiByCategory}
                  />
                </StepShell>}

                {isMobile && product.descriptionHtml && product.descriptionHtml.trim() && (
                  <DescriptionSection descriptionHtml={product.descriptionHtml} collapsible />
                )}

                {/* Add to cart — inline button hidden on mobile (replaced by sticky bar) */}
                {!isMobile && <div style={{ marginTop: 20, marginLeft: 44 }}>
                  <button
                    onClick={handleAddToCart}
                    disabled={!canAddToCart}
                    aria-describedby={unavailableReason ? 'product-unavailable-reason' : undefined}
                    className="product-cart-cta"
                    style={productCartCtaStyle(canAddToCart)}
                    onMouseEnter={e => {
                      if (canAddToCart) {
                        e.currentTarget.style.background = '#000';
                        e.currentTarget.style.color = GOLD;
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = canAddToCart ? GOLD : 'rgba(42,31,20,0.15)';
                      e.currentTarget.style.color = canAddToCart ? '#fff' : MUTED;
                    }}
                  >
                    {unavailableReason ? 'Produkt niedostępny' : isAdding ? 'Sprawdzanie dostępności…' : added ? (
                      <><Check size={15} /> Dodano do koszyka</>
                    ) : (
                      <><ShoppingBag size={15} /> Dodaj do koszyka · {grandTotal.toFixed(2).replace('.', ',')} zł</>
                    )}
                  </button>
                  {submitAttempted && (!deliveryConfirmed || (!isFuneralCategory && !recipientName.trim())) && (
                    <p style={{
                      fontFamily: 'DM Sans, sans-serif', fontSize: 12,
                      color: ERROR_RED, margin: '10px 0 0', textAlign: 'center',
                    }}>
                      Wypełnij obowiązkowe pola, żeby przejść dalej
                    </p>
                  )}
                </div>}

                {/* Kreator CTA */}
                <KreatorCTA />
              </div>

              {/* LEFT — Gallery + description, second in DOM (see note above). order:1 keeps
                  it visually on the left on desktop and — just as importantly — above the
                  details block on mobile's single-column grid, matching the original layout.
                  Sticky positioning stays desktop-only. */}
              <div style={isMobile ? { order: 1 } : { position: 'sticky', top: 100, order: 1 }}>
                <ProductGallery images={product.images.nodes} tags={product.tags ?? []} />
                {!isMobile && product.descriptionHtml && product.descriptionHtml.trim() && (
                  <DescriptionSection descriptionHtml={product.descriptionHtml} />
                )}
              </div>
            </div>
          </div>

          {/* ── Full-width sections ── */}

          {/* Reviews — CREAM bg */}
          <ReviewsSection />

          {/* CREAM → CREAM2 */}

          {/* Related products — CREAM2 bg */}
          <RelatedProducts currentHandle={handle} />

          {/* ── Sticky mobile CTA bar ── */}
          {isMobile && (
            <div style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 150,
              background: '#fff',
              borderTop: '1px solid rgba(42,31,20,0.08)',
              boxShadow: '0 -4px 18px rgba(0,0,0,0.08)',
              padding: '10px 14px',
              paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
            }}>
              {submitAttempted && (!deliveryConfirmed || (!isFuneralCategory && !recipientName.trim())) && (
                <p style={{
                  fontFamily: 'DM Sans, sans-serif', fontSize: 11,
                  color: ERROR_RED, margin: '0 0 6px', textAlign: 'center',
                }}>
                  Wypełnij obowiązkowe pola, żeby przejść dalej
                </p>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 0, flexShrink: 0 }}>
                  <p style={{
                    fontFamily: 'DM Sans, sans-serif', fontSize: 20, fontWeight: 700,
                    color: GOLD, margin: 0, lineHeight: 1.1, whiteSpace: 'nowrap',
                  }}>
                    {selected ? `${grandTotal.toFixed(2).replace('.', ',')} zł` : minVariantPrice}
                  </p>
                  {deliveryConfirmed && deliveryDetails && (
                    <p style={{
                      fontFamily: 'DM Sans, sans-serif', fontSize: 10, fontWeight: 500,
                      color: MUTED, margin: 0, lineHeight: 1.2, whiteSpace: 'nowrap',
                    }}>
                      {deliveryDetails.cost.cost === 0
                        ? 'w tym dostawa bezpłatna'
                        : `w tym dostawa ${deliveryDetails.cost.cost.toFixed(2).replace('.', ',')} zł`}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleAddToCart}
                  disabled={!canAddToCart}
                  aria-describedby={unavailableReason ? 'product-unavailable-reason' : undefined}
                  className="product-cart-cta"
                  style={{ ...productCartCtaStyle(canAddToCart), flex: 1, minWidth: 0 }}
                  onMouseEnter={e => {
                    if (canAddToCart) {
                      e.currentTarget.style.background = '#000';
                      e.currentTarget.style.color = GOLD;
                    }
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = canAddToCart ? GOLD : 'rgba(42,31,20,0.15)';
                    e.currentTarget.style.color = canAddToCart ? '#fff' : MUTED;
                  }}
                >
                  {unavailableReason ? 'Niedostępny' : isAdding ? 'Sprawdzanie…' : added
                    ? <><Check size={16} /> Dodano</>
                    : <>Dodaj do koszyka <ShoppingBag size={16} /></>}
                </button>
              </div>
            </div>
          )}

        </>
      )}
      <Footer />
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .product-cart-cta:focus-visible {
          outline: 3px solid rgba(224,85,144,0.38);
          outline-offset: 3px;
        }
      `}</style>
    </div>
  );
}
